import { Drawer, Steps } from "antd";
import { Formik } from "formik";
import { useEffect, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { PiArrowLeft, PiCheckCircle, PiPencilSimple, PiPlus, PiTrash, PiTruckFill, PiWarningCircle, PiXBold } from "react-icons/pi";
import ReactQuill from "react-quill-new";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "../../components/Breadcrumbs";
import Button from "../../components/button";
import FileUpload from "../../components/FileUpload";
import Input from "../../components/input";
import Select from "../../components/Select";
import { apiService } from "../../services/apiService";
import { fetchBanksDropdown, fetchCustomerDropdown } from "../../store/Slices/dropdownSlice";
import { fetchVehicleTypesDropdown } from "../../store/Slices/VehicleDropdownSlice";
import "../../styles/Access.css";
import "../../styles/AssetsRegistration.css";
import "../../styles/DriverSafety.css";
import "../../styles/Input.css";
import "../../styles/Issue.css";
import "../../styles/TransporterSteps.css";
import "../../styles/Trip.css";
import "../../styles/TripCostEstimation.css";
import { paymentTermsOptions } from "../../utils/Dropdowns";
import { formats, modules } from "../../utils/helper";
import { errorNotify, successNotify } from "../../utils/toast";
import {
  fixedTypeOptions,
  getFixedTypeLabel,
  getTripDataTypeLabel,
  getVariableTypeLabel,
  storageTripDataTypeOptions,
  tripDataTypeOptions
} from "./createContract.enums";
import { createContractSchema } from "./createContract.schema";

const shortageTypeOptions = [
  { label: "Damaged Goods", value: "damaged_goods" },
  { label: "Fuel Shortage", value: "fuel_shortage" },
  { label: "Late Delivery", value: "late_delivery" },
  { label: "Other Deduction", value: "other_deduction" },
];

const shortageBasisOptions = [
  { label: "Per Trip", value: "per_trip" },
  { label: "Per Liter", value: "per_liter" },
  { label: "Per KM", value: "per_km" },
  { label: "Fixed", value: "fixed" },
];

const componentTypeOptions = [
  { label: "Per KM", value: "per_km" },
  { label: "Per Trip", value: "per_trip" },
  { label: "Per Ton", value: "per_ton" },
  { label: "Fuel Adjustment", value: "fuel_adjustment" },
  { label: "Parking (Fixed)", value: "parking" },
  { label: "Driver Wages (Fixed)", value: "driver_wages" },
  { label: "Toll (Fixed)", value: "toll" },
  { label: "Loading (Fixed)", value: "loading" },
  { label: "Unloading (Fixed)", value: "unloading" },
];

const getShortageTypeLabel = (value) => {
  const matched = shortageTypeOptions.find((o) => o.value === value);
  return matched ? matched.label : value;
};

const getShortageBasisLabel = (value) => {
  const matched = shortageBasisOptions.find((o) => o.value === value);
  return matched ? matched.label : value;
};

const syncShortagesFormula = (chargeIndex, currentShortages, setFieldValue) => {
  const tokens = [];
  currentShortages.forEach((shortage, index) => {
    if (index > 0) {
      tokens.push({
        id: `formula-operator-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        type: "operator",
        value: "+",
        label: "+",
      });
    }

    const label = `${getShortageTypeLabel(shortage.shortageType)} (${shortage.deductionValue})`;
    tokens.push({
      id: `formula-hardcodedValue-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      type: "hardcodedValue",
      value: String(shortage.deductionValue),
      label: label,
    });

    if (shortage.basis !== "fixed") {
      tokens.push({
        id: `formula-operator-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        type: "operator",
        value: "*",
        label: "*",
      });

      let basisVar = "shortage_qty";
      let basisLabel = "Shortage Qty";

      if (shortage.basis === "per_km") {
        basisVar = "odometer_distance";
        basisLabel = "Odometer Distance";
      }

      tokens.push({
        id: `formula-tripData-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        type: "tripData",
        value: basisVar,
        label: basisLabel,
      });
    }
  });

  setFieldValue(`charges[${chargeIndex}].shortagesFormulaTokens`, tokens, false);

  if (tokens.length > 0) {
    const displayText = tokens
      .map((token) => {
        if (token.type === "operator") return token.value;
        return token.label || token.value;
      })
      .join(" ");

    setFieldValue(
      `charges[${chargeIndex}].shortagesFormulaRules`,
      [
        {
          id: `rule-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          tokens: tokens.map((t) => ({ ...t })),
          displayText,
        },
      ],
      false
    );
  } else {
    setFieldValue(`charges[${chargeIndex}].shortagesFormulaRules`, [], false);
  }
};

const TEMPLATE_INFORMATION_STEP = 0;
const COMPONENTS_FORMULA_STEP = 1;
const ATTACHMENTS_STEP = 2;
const PAYMENT_MODE_STEP = 3;

const statusOptions = [
  { label: "Pending", value: "pending" },
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

const billingFrequencyOptions = [
  { label: "Weekly", value: "weekly" },
  { label: "Fortnightly ", value: "fortnight" },
  { label: "Monthly", value: "monthly" },
  { label: "Yearly", value: "yearly" },
];

const paymentMethodOptions = [
  { label: "Bank Transfer", value: "bank_transfer" },
  { label: "Cash", value: "cash" },
];

const resolveDropdownCollection = (source, candidateKeys = []) => {
  if (Array.isArray(source)) return source;
  if (!source || typeof source !== "object") return [];

  const normalizedCandidateKeys = candidateKeys.map((key) =>
    String(key || "").toLowerCase()
  );

  for (const [key, value] of Object.entries(source)) {
    if (!Array.isArray(value)) continue;

    const normalizedKey = String(key || "").toLowerCase();
    if (
      normalizedCandidateKeys.includes(normalizedKey) ||
      normalizedCandidateKeys.some((candidate) => normalizedKey.includes(candidate))
    ) {
      return value;
    }
  }

  return [];
};

const normalizeSelectOptions = (
  source,
  { valueKeys = ["value", "id"], labelKeys = ["label", "name", "title"] } = {}
) =>
  (Array.isArray(source) ? source : [])
    .map((item, index) => {
      if (item === null || item === undefined) return null;

      if (typeof item === "string" || typeof item === "number") {
        return {
          value: item,
          label: String(item),
        };
      }

      if (typeof item !== "object") return null;

      const resolvedValue = valueKeys.reduce(
        (result, key) => (result !== undefined ? result : item?.[key]),
        undefined
      );
      const resolvedLabel = labelKeys.reduce(
        (result, key) => (result !== undefined ? result : item?.[key]),
        undefined
      );

      if (resolvedValue === undefined && resolvedLabel === undefined) return null;

      return {
        ...item,
        value: resolvedValue ?? resolvedLabel ?? index + 1,
        label: String(resolvedLabel ?? resolvedValue ?? `Option ${index + 1}`),
      };
    })
    .filter(
      (option) =>
        option &&
        option.value !== undefined &&
        option.value !== null &&
        String(option.label || "").trim()
    );

const stripHtml = (value) =>
  String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\u200B/g, "")
    .replace(/\s+/g, " ")
    .trim();

const normalizeToArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined || value === "") return [];
  return [value];
};

const resolveUploadedFilePath = (fileEntry) => {
  if (typeof fileEntry === "string") return String(fileEntry).trim();
  if (!fileEntry || typeof fileEntry !== "object") return "";

  return String(
    fileEntry?.fileName ||
    fileEntry?.file_path ||
    fileEntry?.file_name ||
    fileEntry?.path ||
    fileEntry?.url ||
    fileEntry?.file ||
    fileEntry?.image ||
    (typeof fileEntry?.data === "string" ? fileEntry.data : "") ||
    fileEntry?.data?.fileName ||
    fileEntry?.data?.file_path ||
    fileEntry?.data?.file_name ||
    fileEntry?.data?.path ||
    fileEntry?.data?.url ||
    fileEntry?.data?.file ||
    fileEntry?.data?.image ||
    ""
  ).trim();
};

const parseIdOrRaw = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? value : numericValue;
};

const parseNumberOrZero = (value) => {
  if (value === "" || value === null || value === undefined) return 0;
  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? 0 : numericValue;
};

const formatContractDateTime = (value, useEndOfDay = false) => {
  const normalizedValue = String(value || "").trim();
  if (!normalizedValue) return "";
  return `${normalizedValue} ${useEndOfDay ? "23:59:59" : "00:00:00"}`;
};

const normalizeFormulaVariableValue = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const buildContractDescription = (values = {}) =>
  [
    stripHtml(values?.description),
    stripHtml(values?.notes),
  ]
    .filter(Boolean)
    .join(" | ");

const getPricingTemplateInitialValues = (generatedId) => ({
  systemId: generatedId || "PT-00041",
  templateName: "",
  customer: "",
  status: "",
  effectiveFrom: "",
  effectiveTo: "",
  description: "",
  attachments: [],
  vehicleTypes: [],
  charges: [],
  billingFrequency: "",
  paymentMethod: "",
  paymentTerms: "",
  bank: "",
  accountTitle: "",
  iban: "",
  notes: "",
});

const createFixedChargeRow = () => ({
  id: `fixed-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  fixedType: "",
  fixedCharge: "",
});

const createVehicleTypeCharge = (vehicleTypeOption = {}) => {
  const resolvedValue =
    vehicleTypeOption && typeof vehicleTypeOption === "object"
      ? vehicleTypeOption?.value ?? ""
      : vehicleTypeOption;
  const resolvedLabel =
    vehicleTypeOption && typeof vehicleTypeOption === "object"
      ? vehicleTypeOption?.label || String(resolvedValue || "")
      : String(vehicleTypeOption || "");

  return {
    id: `${String(resolvedValue || resolvedLabel || "vehicle")}-${Date.now()}`,
    vehicleTypeId: resolvedValue,
    vehicleType: resolvedLabel,
    variableType: "",
    variableRate: "",
    numberOfVehicle: "",
    monthlyCharges: "",
    fixedCharges: [],
    formulaTripData: "",
    formulaTokens: [],
    formulaRules: [],
    formulaHardcodedValue: "",
    shortagesFormulaTripData: "",
    shortagesFormulaTokens: [],
    shortagesFormulaRules: [],
    shortagesFormulaHardcodedValue: "",
    chargeShortage: false,
  };
};

const createFormulaToken = (type, value, label = "", metadata = {}) => ({
  id: `formula-${type}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  type,
  value,
  label,
  ...metadata,
});

const createFormulaRule = (tokens = [], displayText = "") => ({
  id: `rule-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  tokens,
  displayText,
});

const isFormulaOperand = (token = {}) =>
  ["tripData", "variableRate", "fixedCharge", "hardcodedValue"].includes(String(token?.type || ""));

const validateFormulaTokens = (tokens = []) => {
  const normalizedTokens = Array.isArray(tokens) ? tokens.filter(Boolean) : [];

  if (!normalizedTokens.length) {
    return "Formula is required";
  }

  let bracketBalance = 0;
  let previousKind = "start";

  for (let index = 0; index < normalizedTokens.length; index += 1) {
    const token = normalizedTokens[index];
    const tokenValue = String(token?.value || "");

    if (isFormulaOperand(token)) {
      if (previousKind === "operand" || previousKind === "close") {
        return "Add an operator between formula values";
      }
      previousKind = "operand";
      continue;
    }

    if (String(token?.type || "") === "operator" && tokenValue === "(") {
      if (previousKind === "operand" || previousKind === "close") {
        return "Add an operator before opening bracket";
      }
      bracketBalance += 1;
      previousKind = "open";
      continue;
    }

    if (String(token?.type || "") === "operator" && tokenValue === ")") {
      if (bracketBalance <= 0) {
        return "Closing bracket is misplaced";
      }
      if (["start", "operator", "open"].includes(previousKind)) {
        return "Closing bracket cannot appear here";
      }
      bracketBalance -= 1;
      previousKind = "close";
      continue;
    }

    if (String(token?.type || "") === "operator") {
      if (["start", "operator", "open"].includes(previousKind)) {
        return `Operator ${tokenValue} cannot be used here`;
      }
      previousKind = "operator";
      continue;
    }
  }

  if (bracketBalance !== 0) {
    return "Brackets are not balanced";
  }

  if (["operator", "open"].includes(previousKind)) {
    return "Formula cannot end with an operator";
  }

  return "";
};

const syncChargesWithVehicleTypes = (
  currentCharges = [],
  vehicleTypes = [],
  vehicleTypeOptions = []
) => {
  const normalizedTypes = (Array.isArray(vehicleTypes) ? vehicleTypes : []).filter(
    (value) => value !== "" && value !== null && value !== undefined
  );

  return normalizedTypes.map((vehicleTypeValue) => {
    const matchedOption = (Array.isArray(vehicleTypeOptions) ? vehicleTypeOptions : []).find(
      (option) => String(option?.value ?? "") === String(vehicleTypeValue ?? "")
    );
    const resolvedVehicleTypeOption = matchedOption || {
      value: vehicleTypeValue,
      label: String(vehicleTypeValue || ""),
    };
    const existingCharge = (Array.isArray(currentCharges) ? currentCharges : []).find(
      (charge) =>
        String(charge?.vehicleTypeId ?? charge?.vehicleType ?? "") ===
        String(vehicleTypeValue ?? "")
    );

    return existingCharge
      ? {
        ...createVehicleTypeCharge(resolvedVehicleTypeOption),
        ...existingCharge,
        vehicleTypeId: resolvedVehicleTypeOption.value,
        vehicleType: resolvedVehicleTypeOption.label,
        fixedCharges: Array.isArray(existingCharge?.fixedCharges)
          ? existingCharge.fixedCharges
          : [],
      }
      : createVehicleTypeCharge(resolvedVehicleTypeOption);
  });
};

const renderFieldLabel = (label, required = true) => (
  <span className="assets_vehicle_field_label">
    <span>{label}</span>
    {required ? <span className="assets_vehicle_field_required">*</span> : null}
  </span>
);

const pickOption = (options = [], value = "") =>
  (Array.isArray(options) ? options : []).find(
    (option) => String(option?.value ?? "") === String(value ?? "")
  ) || null;

const pickMultiOptions = (options = [], values = []) =>
  (Array.isArray(options) ? options : []).filter((option) =>
    (Array.isArray(values) ? values : []).some(
      (value) => String(value ?? "") === String(option?.value ?? "")
    )
  );

const buildFormulaPayloadTokens = (
  charge = {},
  { rulesKey = "formulaRules", tokensKey = "formulaTokens" } = {}
) => {
  const sourceTokens =
    Array.isArray(charge?.[rulesKey]?.[0]?.tokens) && charge[rulesKey][0].tokens.length
      ? charge[rulesKey][0].tokens
      : Array.isArray(charge?.[tokensKey])
        ? charge[tokensKey]
        : [];

  return sourceTokens
    .map((token, index) => {
      const tokenType = String(token?.type || "").trim();

      if (tokenType === "operator") {
        const operatorValue = String(token?.value || "").trim();
        return operatorValue
          ? {
            position: index + 1,
            type: "operator",
            value: operatorValue,
            label: String(token?.label || operatorValue).trim(),
          }
          : null;
      }

      if (tokenType === "tripData") {
        const tokenLabel = String(
          token?.label || getTripDataTypeLabel(token?.value) || token?.value || ""
        ).trim();
        const tokenValue = normalizeFormulaVariableValue(token?.value || tokenLabel);
        return tokenValue
          ? {
            position: index + 1,
            type: "trip_data",
            value: tokenValue,
            label: tokenLabel || tokenValue,
          }
          : null;
      }

      if (tokenType === "variableRate") {
        const tokenValue = String(token?.value || charge?.variableRate || "").trim();
        return tokenValue
          ? {
            position: index + 1,
            type: "constant",
            value: tokenValue,
            label: String(
              token?.label ||
              getVariableTypeLabel(charge?.variableType) ||
              "Variable"
            ).trim(),
          }
          : null;
      }


      if (tokenType === "fixedCharge") {
        return {
          position: index + 1,
          type: "fixed_charge",
          value: String(token?.value || "").trim(),
          label: String(token?.label || "").trim(),
        };
      }

      if (tokenType === "hardcodedValue") {
        return {
          position: index + 1,
          type: "constant",
          value: String(token?.value || "").trim(),
          label: String(token?.label || token?.value || "Value").trim(),
        };
      }

      const fallbackLabel = String(token?.label || token?.value || "").trim();
      const fallbackValue = normalizeFormulaVariableValue(token?.value || fallbackLabel);

      return fallbackValue
        ? {
          position: index + 1,
          type: "constant",
          value: fallbackValue,
          label: fallbackLabel || fallbackValue,
        }
        : null;
    })
    .filter(Boolean);
};

const pricingTemplateSteps = [
  { title: "Contract Information" },
  { title: "Components & Formula Builder" },
  { title: "Attachments" },
  { title: "Payment Mode" },
];

const pricingTemplateStepFields = [
  ["templateName", "customer", "status", "effectiveFrom", "effectiveTo"],
  ["vehicleTypes"],
  ["attachments"],
  ["billingFrequency", "paymentMethod", "paymentTerms", "bank", "accountTitle", "iban"],
];

const STEP_TWO_BLOCK_TAB_SIZE = 80;

const compactSelectStyles = {
  control: (baseStyles, state) => ({
    ...baseStyles,
    minHeight: "20px",
    height: "20px",
    border: "none",
    borderColor: "transparent",
    boxShadow: "none",
    outline: "none",
    "&:hover": {
      border: "none",
      borderColor: "transparent",
    },
  }),
  valueContainer: (baseStyles) => ({
    ...baseStyles,
    height: "20px",
    padding: "0 0 0 6px",
  }),
  dropdownIndicator: (baseStyles) => ({
    ...baseStyles,
    padding: "0",
  }),
  indicatorsContainer: (baseStyles) => ({
    ...baseStyles,
    height: "20px",
  }),
  singleValue: (baseStyles) => ({
    ...baseStyles,
    lineHeight: "20px",
  }),
  placeholder: (baseStyles) => ({
    ...baseStyles,
    lineHeight: "20px",
  }),
  input: (baseStyles) => ({
    ...baseStyles,
    margin: "0",
    padding: "0",
  }),
};

const CreateContract = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const stepsSidebarRef = useRef(null);
  const [currentFormStep, setCurrentFormStep] = useState(
    TEMPLATE_INFORMATION_STEP
  );
  const [selectedAddVehicleType, setSelectedAddVehicleType] = useState("");
  const [nextSystemId, setNextSystemId] = useState("PT-00041");
  const [editingChargeIndex, setEditingChargeIndex] = useState(null);
  const [activeCategory, setActiveCategory] = useState("tripData");
  const [activeShortagesCategory, setActiveShortagesCategory] = useState(null);
  const [tempChargeType, setTempChargeType] = useState("");
  const [tempChargeValue, setTempChargeValue] = useState("");
  const [tempShortageType, setTempShortageType] = useState("");
  const [tempShortageValue, setTempShortageValue] = useState("");
  const [tempShortageBasis, setTempShortageBasis] = useState("");
  const {
    customerDropdownData = [],
    banksDropdownData = [],
    loading: customerDropdownLoading,
  } = useSelector(
    (state) => state.dropdowns || {}
  );
  const {
    vehicleTypesDropdownData = [],
    vehicleDropdownLoadingCount = 0,
  } = useSelector((state) => state.vehicleDropdowns || {});
  const customerOptions = normalizeSelectOptions(
    resolveDropdownCollection(customerDropdownData, ["customer"]),
    {
      valueKeys: ["value", "id", "customer_id"],
      labelKeys: ["label", "name", "customer_name", "title"],
    }
  );
  const vehicleTypeDropdownOptions = normalizeSelectOptions(
    resolveDropdownCollection(vehicleTypesDropdownData, ["vehicletype", "types"]),
    {
      valueKeys: ["value", "id", "vehicle_type_id"],
      labelKeys: ["label", "name", "title", "vehicle_type_name"],
    }
  );

  useEffect(() => {
    dispatch(fetchCustomerDropdown());
    dispatch(fetchBanksDropdown());
    dispatch(fetchVehicleTypesDropdown());
  }, [dispatch]);

  useEffect(() => {
    let isMounted = true;
    const fetchNextId = async () => {
      try {
        const response = await apiService.getContractsPaginated({
          page: 1,
          limit: 1000,
        });
        const payload = response?.data || response || {};
        const results = Array.isArray(payload?.results) ? payload.results : [];

        let maxId = 0;
        results.forEach((contract) => {
          const templateId = contract.contracts_template_id || contract.templateId || contract.contracts_id || "";
          const match = templateId.match(/(\d+)/);
          if (match) {
            const num = parseInt(match[1], 10);
            if (num > maxId) {
              maxId = num;
            }
          }
        });

        if (maxId > 0 && isMounted) {
          const nextNum = maxId + 1;
          const generatedId = `PT-${String(nextNum).padStart(5, "0")}`;
          setNextSystemId(generatedId);
        }
      } catch (error) {
        console.error("Failed to load contracts for ID generation", error);
      }
    };
    fetchNextId();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const sidebarElement = stepsSidebarRef.current;
    if (!sidebarElement) return;

    sidebarElement.querySelectorAll("button, a, [tabindex]").forEach((element) => {
      element.setAttribute("tabindex", "-1");
    });
  }, [currentFormStep]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (activeCategory && !event.target.closest(".tab-popover-wrapper")) {
        setActiveCategory(null);
      }
      if (activeShortagesCategory && !event.target.closest(".tab-popover-wrapper")) {
        setActiveShortagesCategory(null);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [activeCategory, activeShortagesCategory]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const payload = {
        name: String(values.templateName || "").trim(),
        customer_id: parseIdOrRaw(values.customer),
        start_at: formatContractDateTime(values.effectiveFrom),
        expire_at: formatContractDateTime(values.effectiveTo, true),
        status: String(values.status || "").trim().toLowerCase() || "pending",
        description: buildContractDescription(values),
        billing_frequency: String(values.billingFrequency || "").trim(),
        payment_method: String(values.paymentMethod || "").trim(),
        payment_term: String(values.paymentTerms || "").trim(),
        bank_name: String(values.bank || "").trim(),
        account_title: String(values.accountTitle || "").trim(),
        iban_number: String(values.iban || "").trim(),
        files: normalizeToArray(values.attachments)
          .map((file) => resolveUploadedFilePath(file))
          .filter(Boolean),
        contract_template_vehicle_types: normalizeToArray(values.charges).map((charge) => ({
          vehicle_type_id: parseIdOrRaw(charge?.vehicleTypeId),
          no_of_vehicle: parseNumberOrZero(charge?.numberOfVehicle),
          monthly_rental: parseNumberOrZero(charge?.monthlyCharges),
          charge_shortage: !!charge?.chargeShortage,
          variable_charge_type: String(charge?.variableType || "").trim(),
          variable_charge_rate: parseNumberOrZero(charge?.variableRate),
          contract_template_vehicle_types_charges: normalizeToArray(charge?.fixedCharges).map(
            (fixedCharge) => ({
              fix_charge_type: String(fixedCharge?.fixedType || "").trim(),
              fix_charge_rate: parseNumberOrZero(fixedCharge?.fixedCharge),
            })
          ),
          formula_tokens: buildFormulaPayloadTokens(charge),
          shortages_formula_tokens: buildFormulaPayloadTokens(charge, {
            rulesKey: "shortagesFormulaRules",
            tokensKey: "shortagesFormulaTokens",
          }),
        })),
      };

      await apiService.createContract(payload);
      successNotify("Contract created successfully");
      navigate("/contract");
    } catch (error) {
      errorNotify(error || "Failed to create contract");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      enableReinitialize={true}
      initialValues={getPricingTemplateInitialValues(nextSystemId)}
      validationSchema={createContractSchema}
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        setFieldValue,
        setFieldTouched,
        setFieldError,
        validateForm,
        handleSubmit,
        isSubmitting,
        setTouched,
        submitCount,
      }) => {
        const updateAttachments = (updater) => {
          const nextFiles =
            typeof updater === "function" ? updater(values.attachments || []) : updater;
          setFieldValue("attachments", nextFiles || []);
          setFieldTouched("attachments", true, false);
        };
        const selectedVehicleTypeOptions = pickMultiOptions(
          vehicleTypeDropdownOptions,
          values.vehicleTypes
        );
        const unselectedVehicleTypeOptions = vehicleTypeDropdownOptions.filter(
          (option) => !values.vehicleTypes.includes(option?.value)
        );
        const stepOneTabOrder = {
          templateName: 1,
          customer: 2,
          status: 3,
          effectiveFrom: 4,
          effectiveTo: 5,
          description: 6,
          next: 7,
          notes: 8,
        };
        const stepTwoTabOrder = {
          billingFrequency: 1,
          paymentMethod: 2,
          paymentTerms: 3,
          bank: 4,
          accountTitle: 5,
          iban: 6,
          next: 7,
          notes: 8,
        };
        const stepAttachmentsTabOrder = {
          attachments: 1,
          next: 2,
          notes: 3,
        };
        const getStepTwoBlockBaseIndex = (index) => index * STEP_TWO_BLOCK_TAB_SIZE + 1;
        const getStepTwoFieldTabIndex = (index, offset) =>
          getStepTwoBlockBaseIndex(index) + offset;
        const getFixedChargeTabIndex = (chargeIndex, fixedIndex, offset) =>
          getStepTwoBlockBaseIndex(chargeIndex) + 7 + fixedIndex * 2 + offset;
        const getFormulaTabIndex = (chargeIndex, offset) =>
          getStepTwoBlockBaseIndex(chargeIndex) + 20 + offset;
        const getShortagesFormulaTabIndex = (chargeIndex, offset) =>
          getStepTwoBlockBaseIndex(chargeIndex) + 40 + offset;
        const stepTwoActionsTabIndex =
          (values.charges?.length || 0) * STEP_TWO_BLOCK_TAB_SIZE + 1;
        const stepThreeFooterActionsTabIndex = stepTwoActionsTabIndex + 10;
        const stepThreeNotesTabIndex = stepThreeFooterActionsTabIndex + 2;

        const handleAddFixedCharge = (chargeIndex) => {
          const currentCharge = values?.charges?.[chargeIndex] || {};
          const currentFixedCharges = Array.isArray(values?.charges?.[chargeIndex]?.fixedCharges)
            ? values.charges[chargeIndex].fixedCharges
            : [];

          setFieldValue(
            `charges[${chargeIndex}].fixedCharges`,
            [...currentFixedCharges, createFixedChargeRow()],
            false
          );
          setFieldTouched(`charges[${chargeIndex}].fixedCharges`, true);
        };

        const handleRemoveFixedCharge = (chargeIndex, fixedIndex) => {
          const currentFixedCharges = Array.isArray(values?.charges?.[chargeIndex]?.fixedCharges)
            ? values.charges[chargeIndex].fixedCharges
            : [];
          const removedFixedChargeId = currentFixedCharges?.[fixedIndex]?.id || "";
          const nextFixedCharges = currentFixedCharges.filter(
            (_, index) => index !== fixedIndex
          );

          const currentFormulaTokens = Array.isArray(values?.charges?.[chargeIndex]?.formulaTokens)
            ? values.charges[chargeIndex].formulaTokens
            : [];
          const currentFormulaRules = Array.isArray(values?.charges?.[chargeIndex]?.formulaRules)
            ? values.charges[chargeIndex].formulaRules
            : [];

          setFieldValue(
            `charges[${chargeIndex}].fixedCharges`,
            nextFixedCharges,
            false
          );
        };

        const getFixedTypeDropdownOptions = (currentFixedCharges = [], currentFixedIndex = 0) => {
          const selectedTypes = currentFixedCharges
            .map((entry, index) => (index === currentFixedIndex ? "" : entry?.fixedType || ""))
            .filter(Boolean);

          return fixedTypeOptions.filter(
            (option) =>
              String(option?.value || "") ===
              String(currentFixedCharges?.[currentFixedIndex]?.fixedType || "") ||
              !selectedTypes.includes(option?.value)
          );
        };


        const resolveFormulaTokenLabel = (chargeIndex, token = {}) => {
          if (String(token?.type || "") === "operator") {
            return token?.label || token?.value || "";
          }

          if (String(token?.type || "") === "variableRate") {
            const val = token?.value || values?.charges?.[chargeIndex]?.variableRate || "";
            const typeLabel = getVariableTypeLabel(values?.charges?.[chargeIndex]?.variableType) || token?.label || "Variable";
            return val ? `${typeLabel} (${val})` : typeLabel;
          }

          if (String(token?.type || "") === "fixedCharge") {
            return token?.label || `Charge (${token?.value || "0"})`;
          }

          if (String(token?.type || "") === "hardcodedValue") {
            return token?.value || "0";
          }

          return token?.label || token?.value || "";
        };

        const appendFormulaToken = (chargeIndex, token) => {
          const currentTokens = Array.isArray(values?.charges?.[chargeIndex]?.formulaTokens)
            ? values.charges[chargeIndex].formulaTokens
            : [];

          setFieldError(`charges[${chargeIndex}].formulaTokens`, undefined);
          setFieldValue(
            `charges[${chargeIndex}].formulaTokens`,
            [...currentTokens, token],
            false
          );
        };


        const handleAddTripDataToFormula = (chargeIndex) => {
          const selectedTripData = values?.charges?.[chargeIndex]?.formulaTripData || "";
          const selectedTripDataOption = tripDataTypeOptions.find(
            (option) => String(option?.value || "") === String(selectedTripData)
          );

          if (!selectedTripDataOption) {
            setFieldTouched(`charges[${chargeIndex}].formulaTripData`, true, false);
            return;
          }

          appendFormulaToken(
            chargeIndex,
            createFormulaToken(
              "tripData",
              selectedTripDataOption.value,
              selectedTripDataOption.label
            )
          );
          setFieldValue(`charges[${chargeIndex}].formulaTripData`, "", false);
        };

        const handleAddVariableRateToFormula = (chargeIndex) => {
          const selectedVariableRate = values?.charges?.[chargeIndex]?.variableRate || "";
          const selectedVariableType = values?.charges?.[chargeIndex]?.variableType || "";

          if (!String(selectedVariableRate || "").trim()) {
            setFieldTouched(`charges[${chargeIndex}].variableRate`, true, false);
            return;
          }

          appendFormulaToken(
            chargeIndex,
            createFormulaToken(
              "variableRate",
              selectedVariableRate,
              `${getVariableTypeLabel(selectedVariableType) || "Variable"} (${selectedVariableRate})`
            )
          );
        };

        const handleAddFixedChargeToFormula = (chargeIndex, fixedIndex) => {
          const fixedCharge = values?.charges?.[chargeIndex]?.fixedCharges?.[fixedIndex];
          if (!fixedCharge || !fixedCharge.fixedType || !fixedCharge.fixedCharge) return;

          appendFormulaToken(
            chargeIndex,
            createFormulaToken(
              "fixedCharge",
              fixedCharge.fixedCharge,
              `${getFixedTypeLabel(fixedCharge.fixedType)} (${fixedCharge.fixedCharge})`,
              { fixedType: fixedCharge.fixedType }
            )
          );
        };

        const handleAddHardcodedValueToFormula = (chargeIndex) => {
          const hardcodedValue = values?.charges?.[chargeIndex]?.formulaHardcodedValue || "";
          if (!String(hardcodedValue || "").trim()) {
            setFieldTouched(`charges[${chargeIndex}].formulaHardcodedValue`, true, false);
            return;
          }

          appendFormulaToken(
            chargeIndex,
            createFormulaToken("hardcodedValue", hardcodedValue, hardcodedValue)
          );
          setFieldValue(`charges[${chargeIndex}].formulaHardcodedValue`, "", false);
        };

        const handleAddOperatorToFormula = (chargeIndex, operator) => {
          appendFormulaToken(chargeIndex, createFormulaToken("operator", operator, operator));
        };

        const handleRemoveFormulaToken = (chargeIndex, tokenIndex) => {
          const currentTokens = Array.isArray(values?.charges?.[chargeIndex]?.formulaTokens)
            ? values.charges[chargeIndex].formulaTokens
            : [];

          setFieldError(`charges[${chargeIndex}].formulaTokens`, undefined);
          setFieldValue(
            `charges[${chargeIndex}].formulaTokens`,
            currentTokens.filter((_, index) => index !== tokenIndex),
            false
          );
        };

        const handleClearFormula = (chargeIndex) => {
          setFieldError(`charges[${chargeIndex}].formulaTokens`, undefined);
          setFieldValue(`charges[${chargeIndex}].formulaTokens`, [], false);
        };

        const handleBackspaceFormula = (chargeIndex) => {
          const currentTokens = Array.isArray(values?.charges?.[chargeIndex]?.formulaTokens)
            ? values.charges[chargeIndex].formulaTokens
            : [];

          setFieldError(`charges[${chargeIndex}].formulaTokens`, undefined);
          setFieldValue(
            `charges[${chargeIndex}].formulaTokens`,
            currentTokens.slice(0, -1),
            false
          );
        };

        const buildFormulaDisplay = (chargeIndex, tokens = []) =>
          (Array.isArray(tokens) ? tokens : [])
            .map((token) => resolveFormulaTokenLabel(chargeIndex, token))
            .join(" ");

        const handleAddFormulaRule = (chargeIndex) => {
          const currentTokens = Array.isArray(values?.charges?.[chargeIndex]?.formulaTokens)
            ? values.charges[chargeIndex].formulaTokens
            : [];
          const formulaError = validateFormulaTokens(currentTokens);

          if (formulaError) {
            setFieldTouched(`charges[${chargeIndex}].formulaTokens`, true, false);
            setFieldError(`charges[${chargeIndex}].formulaTokens`, formulaError);
            return;
          }

          setFieldError(`charges[${chargeIndex}].formulaTokens`, undefined);
          setFieldValue(
            `charges[${chargeIndex}].formulaRules`,
            [
              createFormulaRule(
                currentTokens.map((token) => ({ ...token })),
                buildFormulaDisplay(chargeIndex, currentTokens)
              ),
            ],
            false
          );
          setFieldValue(`charges[${chargeIndex}].formulaTokens`, [], false);
          setFieldValue(`charges[${chargeIndex}].formulaTripData`, "", false);
        };

        const handleRemoveFormulaRule = (chargeIndex, ruleIndex) => {
          const currentRules = Array.isArray(values?.charges?.[chargeIndex]?.formulaRules)
            ? values.charges[chargeIndex].formulaRules
            : [];

          setFieldError(`charges[${chargeIndex}].formulaTokens`, undefined);
          setFieldValue(
            `charges[${chargeIndex}].formulaRules`,
            currentRules.filter((_, index) => index !== ruleIndex),
            false
          );
          setFieldValue(`charges[${chargeIndex}].formulaTokens`, [], false);
          setFieldValue(`charges[${chargeIndex}].formulaTripData`, "", false);
        };

        const handleEditFormulaRule = (chargeIndex, ruleIndex) => {
          const currentRules = Array.isArray(values?.charges?.[chargeIndex]?.formulaRules)
            ? values.charges[chargeIndex].formulaRules
            : [];
          const selectedRule = currentRules?.[ruleIndex];

          if (!selectedRule) return;

          setFieldError(`charges[${chargeIndex}].formulaTokens`, undefined);
          setFieldValue(
            `charges[${chargeIndex}].formulaTokens`,
            Array.isArray(selectedRule?.tokens)
              ? selectedRule.tokens.map((token) => ({ ...token }))
              : [],
            false
          );
          setFieldValue(
            `charges[${chargeIndex}].formulaRules`,
            currentRules.filter((_, index) => index !== ruleIndex),
            false
          );
        };

        const appendShortagesFormulaToken = (chargeIndex, token) => {
          const currentTokens = Array.isArray(values?.charges?.[chargeIndex]?.shortagesFormulaTokens)
            ? values.charges[chargeIndex].shortagesFormulaTokens
            : [];

          setFieldError(`charges[${chargeIndex}].shortagesFormulaTokens`, undefined);
          setFieldValue(
            `charges[${chargeIndex}].shortagesFormulaTokens`,
            [...currentTokens, token],
            false
          );
        };

        const handleAddTripDataToShortagesFormula = (chargeIndex) => {
          const selectedTripData =
            values?.charges?.[chargeIndex]?.shortagesFormulaTripData || "";
          const selectedTripDataOption = storageTripDataTypeOptions.find(
            (option) => String(option?.value || "") === String(selectedTripData)
          );

          if (!selectedTripDataOption) {
            setFieldTouched(
              `charges[${chargeIndex}].shortagesFormulaTripData`,
              true,
              false
            );
            return;
          }

          appendShortagesFormulaToken(
            chargeIndex,
            createFormulaToken(
              "tripData",
              selectedTripDataOption.value,
              selectedTripDataOption.label
            )
          );
          setFieldValue(`charges[${chargeIndex}].shortagesFormulaTripData`, "", false);
        };

        const handleAddOperatorToShortagesFormula = (chargeIndex, operator) => {
          appendShortagesFormulaToken(
            chargeIndex,
            createFormulaToken("operator", operator, operator)
          );
        };

        const handleRemoveShortagesFormulaToken = (chargeIndex, tokenIndex) => {
          const currentTokens = Array.isArray(values?.charges?.[chargeIndex]?.shortagesFormulaTokens)
            ? values.charges[chargeIndex].shortagesFormulaTokens
            : [];

          setFieldError(`charges[${chargeIndex}].shortagesFormulaTokens`, undefined);
          setFieldValue(
            `charges[${chargeIndex}].shortagesFormulaTokens`,
            currentTokens.filter((_, index) => index !== tokenIndex),
            false
          );
        };

        const handleClearShortagesFormula = (chargeIndex) => {
          setFieldError(`charges[${chargeIndex}].shortagesFormulaTokens`, undefined);
          setFieldValue(`charges[${chargeIndex}].shortagesFormulaTokens`, [], false);
        };

        const handleBackspaceShortagesFormula = (chargeIndex) => {
          const currentTokens = Array.isArray(values?.charges?.[chargeIndex]?.shortagesFormulaTokens)
            ? values.charges[chargeIndex].shortagesFormulaTokens
            : [];

          setFieldError(`charges[${chargeIndex}].shortagesFormulaTokens`, undefined);
          setFieldValue(
            `charges[${chargeIndex}].shortagesFormulaTokens`,
            currentTokens.slice(0, -1),
            false
          );
        };

        const handleAddHardcodedValueToShortagesFormula = (chargeIndex) => {
          const hardcodedValue = values?.charges?.[chargeIndex]?.shortagesFormulaHardcodedValue || "";
          if (!String(hardcodedValue || "").trim()) {
            setFieldTouched(`charges[${chargeIndex}].shortagesFormulaHardcodedValue`, true, false);
            return;
          }

          appendShortagesFormulaToken(
            chargeIndex,
            createFormulaToken("hardcodedValue", hardcodedValue, hardcodedValue)
          );
          setFieldValue(`charges[${chargeIndex}].shortagesFormulaHardcodedValue`, "", false);
        };

        const buildShortagesFormulaDisplay = (chargeIndex, tokens = []) =>
          (Array.isArray(tokens) ? tokens : [])
            .map((token) => resolveFormulaTokenLabel(chargeIndex, token))
            .join(" ");

        const handleAddShortagesFormulaRule = (chargeIndex) => {
          const currentTokens = Array.isArray(values?.charges?.[chargeIndex]?.shortagesFormulaTokens)
            ? values.charges[chargeIndex].shortagesFormulaTokens
            : [];
          const formulaError = validateFormulaTokens(currentTokens);

          if (formulaError) {
            setFieldTouched(`charges[${chargeIndex}].shortagesFormulaTokens`, true, false);
            setFieldError(`charges[${chargeIndex}].shortagesFormulaTokens`, formulaError);
            return;
          }

          setFieldError(`charges[${chargeIndex}].shortagesFormulaTokens`, undefined);
          setFieldValue(
            `charges[${chargeIndex}].shortagesFormulaRules`,
            [
              createFormulaRule(
                currentTokens.map((token) => ({ ...token })),
                buildShortagesFormulaDisplay(chargeIndex, currentTokens)
              ),
            ],
            false
          );
          setFieldValue(`charges[${chargeIndex}].shortagesFormulaTokens`, [], false);
          setFieldValue(`charges[${chargeIndex}].shortagesFormulaTripData`, "", false);
        };

        const handleRemoveShortagesFormulaRule = (chargeIndex, ruleIndex) => {
          const currentRules = Array.isArray(values?.charges?.[chargeIndex]?.shortagesFormulaRules)
            ? values.charges[chargeIndex].shortagesFormulaRules
            : [];

          setFieldError(`charges[${chargeIndex}].shortagesFormulaTokens`, undefined);
          setFieldValue(
            `charges[${chargeIndex}].shortagesFormulaRules`,
            currentRules.filter((_, index) => index !== ruleIndex),
            false
          );
          setFieldValue(`charges[${chargeIndex}].shortagesFormulaTokens`, [], false);
          setFieldValue(`charges[${chargeIndex}].shortagesFormulaTripData`, "", false);
        };

        const handleEditShortagesFormulaRule = (chargeIndex, ruleIndex) => {
          const currentRules = Array.isArray(values?.charges?.[chargeIndex]?.shortagesFormulaRules)
            ? values.charges[chargeIndex].shortagesFormulaRules
            : [];
          const selectedRule = currentRules?.[ruleIndex];

          if (!selectedRule) return;

          setFieldError(`charges[${chargeIndex}].shortagesFormulaTokens`, undefined);
          setFieldValue(
            `charges[${chargeIndex}].shortagesFormulaTokens`,
            Array.isArray(selectedRule?.tokens)
              ? selectedRule.tokens.map((token) => ({ ...token }))
              : [],
            false
          );
          setFieldValue(
            `charges[${chargeIndex}].shortagesFormulaRules`,
            currentRules.filter((_, index) => index !== ruleIndex),
            false
          );
        };

        const validateCurrentStep = async () => {
          const allErrors = await validateForm();
          const activeStepFields = pricingTemplateStepFields[currentFormStep] || [];
          const hasCurrentStepErrors = activeStepFields.some((fieldName) =>
            Object.prototype.hasOwnProperty.call(allErrors, fieldName)
          );

          if (hasCurrentStepErrors) {
            const touchedMap = {};
            activeStepFields.forEach((fieldName) => {
              touchedMap[fieldName] = true;
            });
            setTouched(touchedMap);
          }

          return hasCurrentStepErrors;
        };

        const handleNext = async () => {
          const hasCurrentStepErrors = await validateCurrentStep();
          if (hasCurrentStepErrors) return;
          setCurrentFormStep((previous) =>
            Math.min(previous + 1, pricingTemplateSteps.length - 1)
          );
        };

        const handlePrevious = () => {
          setCurrentFormStep((previous) => Math.max(previous - 1, 0));
        };

        return (
          <div className="create_vehicle_page trip_cost_estimation_create_page pricing_template_create_page">
            <style>{`
              .vehicle-type-select-wrapper .input_container {
                height: 44px !important;
                padding: 5px 8px !important;
                display: flex;
                flex-direction: column;
                justify-content: flex-start;
                box-sizing: border-box;
              }
              .vehicle-type-select-wrapper .input_container label {
                font-size: 10px !important;
                line-height: 12px !important;
                margin: 0 0 2px 0 !important;
                color: #3D3F5C !important;
              }
              .vehicle-type-select-wrapper [class*="-control"],
              .vehicle-type-select-wrapper [class*="control"] {
                border: none !important;
                box-shadow: none !important;
                outline: none !important;
                background: transparent !important;
              }
              .vehicle-type-select-wrapper [class*="-control"]:focus,
              .vehicle-type-select-wrapper [class*="-control"]:focus-within,
              .vehicle-type-select-wrapper [class*="-control"]:active {
                border: none !important;
                box-shadow: none !important;
                outline: none !important;
              }
              .vehicle-type-select-wrapper input:focus,
              .vehicle-type-select-wrapper input:focus-visible {
                outline: none !important;
                box-shadow: none !important;
              }
            `}</style>
            <Breadcrumbs values={["Contract", "Create Contract"]} />

            <div className="page_header access_view">
              <h2>Create Contract</h2>

              <div className="d-flex align-items-center gap-2">
                <Button
                  cancelBtn={() => navigate("/contract")}
                  submitBtn={handleSubmit}
                  submitText={isSubmitting ? "Saving..." : "Save"}
                  type="button"
                  shouldRow={false}
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  cancelTabIndex={98}
                  submitTabIndex={99}
                />
              </div>
            </div>

            <div className="page_container">
              <Row className="driver_page_layout trip_cost_estimation_create_layout">
                <Col lg={9}>
                  <div className="trip_information_main_div driver_form_shell trip_cost_estimation_create_shell">
                    <h5>Contract Creation Playground</h5>

                    <div className="driver_form_content">
                      <h6 className="pricing_template_system_bar">
                        <span className="d-flex align-items-center gap-1">
                          System ID:
                          <span style={{ fontWeight: 500, marginLeft: "4px" }}>
                            {values.systemId}
                          </span>
                        </span>
                      </h6>

                      <div className="d-flex trip_create_steps_layout">
                        <div className="trip_create_steps_sidebar" ref={stepsSidebarRef}>
                          <Steps
                            className="transporter_steps trip_create_steps form_steps_vertical"
                            current={currentFormStep}
                            direction="vertical"
                            items={pricingTemplateSteps}
                            onChange={async (targetStep) => {
                              if (targetStep > currentFormStep) {
                                const hasCurrentStepErrors =
                                  await validateCurrentStep();

                                if (hasCurrentStepErrors) {
                                  return;
                                }
                              }

                              setCurrentFormStep(targetStep);
                            }}
                          />
                        </div>

                        <div className="w-100 mb-3">
                          {currentFormStep === TEMPLATE_INFORMATION_STEP && (
                            <div className="driver_form_section">
                              <Row>
                                <Col md={12}>
                                  <Input
                                    label={renderFieldLabel("Contract Name")}
                                    type="text"
                                    placeholder="Write Contract Name"
                                    name="templateName"
                                    value={values.templateName}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.templateName}
                                    touched={touched.templateName}
                                    tabIndex={stepOneTabOrder.templateName}
                                  />
                                </Col>

                                <Col md={6}>
                                  <Select
                                    label={renderFieldLabel("Customer")}
                                    placeholder="Select Customer"
                                    options={customerOptions}
                                    loading={customerDropdownLoading}
                                    value={pickOption(customerOptions, values.customer)}
                                    name="customer"
                                    onChange={(option) =>
                                      setFieldValue("customer", option?.value || "")
                                    }
                                    onBlur={handleBlur}
                                    error={errors.customer}
                                    touched={touched.customer}
                                    isSearch={true}
                                    tabIndex={stepOneTabOrder.customer}
                                  />
                                </Col>

                                <Col md={6}>
                                  <Select
                                    label={renderFieldLabel("Status")}
                                    placeholder="Select Status"
                                    options={statusOptions}
                                    value={pickOption(statusOptions, values.status)}
                                    name="status"
                                    onChange={(option) =>
                                      setFieldValue("status", option?.value || "")
                                    }
                                    onBlur={handleBlur}
                                    error={errors.status}
                                    touched={touched.status}
                                    isSearch={false}
                                    tabIndex={stepOneTabOrder.status}
                                  />
                                </Col>

                                <Col md={6}>
                                  <Input
                                    label={renderFieldLabel("Effective From")}
                                    type="date"
                                    placeholder="YYYY/MM/DD"
                                    name="effectiveFrom"
                                    value={values.effectiveFrom}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.effectiveFrom}
                                    touched={touched.effectiveFrom}
                                    tabIndex={stepOneTabOrder.effectiveFrom}
                                  />
                                </Col>

                                <Col md={6}>
                                  <Input
                                    label={renderFieldLabel("Effective To")}
                                    type="date"
                                    placeholder="YYYY/MM/DD"
                                    name="effectiveTo"
                                    value={values.effectiveTo}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.effectiveTo}
                                    touched={touched.effectiveTo}
                                    tabIndex={stepOneTabOrder.effectiveTo}
                                  />
                                </Col>

                                <Col md={12}>
                                  <Input
                                    label={renderFieldLabel("Description", false)}
                                    placeholder="Write detail of part if any..."
                                    name="description"
                                    value={values.description}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.description}
                                    touched={touched.description}
                                    quill
                                    setFieldValue={setFieldValue}
                                    className="pricing_template_description_input"
                                    required={false}
                                    tabIndex={stepOneTabOrder.description}
                                  />
                                </Col>

                                <Col md={12}>
                                  <div className="d-flex align-items-center gap-2 pricing_template_step_actions">
                                    <Button
                                      cancelBtn={handlePrevious}
                                      submitBtn={handleNext}
                                      submitText="Next"
                                      type="button"
                                      shouldRow={false}
                                      cancelBtnText="Previous"
                                      cancelBtnDisabled={true}
                                      submitTabIndex={stepOneTabOrder.next}
                                    />
                                  </div>
                                </Col>
                              </Row>
                            </div>
                          )}

                          {currentFormStep === PAYMENT_MODE_STEP && (
                            <div className="driver_form_section pricing_template_step_placeholder">
                              <div className="driver_form_section_header">
                                <h3>Payment Mode</h3>
                                <p>Configure the contract payment settings.</p>
                              </div>

                              <Row className="pricing_template_builder_grid">
                                <Col md={6}>
                                  <Select
                                    label={renderFieldLabel("Billing Frequency")}
                                    placeholder="Select Billing Frequency"
                                    options={billingFrequencyOptions}
                                    value={pickOption(
                                      billingFrequencyOptions,
                                      values.billingFrequency
                                    )}
                                    name="billingFrequency"
                                    onChange={(option) =>
                                      setFieldValue(
                                        "billingFrequency",
                                        option?.value || ""
                                      )
                                    }
                                    onBlur={handleBlur}
                                    error={errors.billingFrequency}
                                    touched={touched.billingFrequency}
                                    isSearch={false}
                                    tabIndex={stepTwoTabOrder.billingFrequency}
                                  />
                                </Col>

                                <Col md={6}>
                                  <Select
                                    label={renderFieldLabel("Payment Method")}
                                    placeholder="Select Payment Method"
                                    options={paymentMethodOptions}
                                    value={pickOption(
                                      paymentMethodOptions,
                                      values.paymentMethod
                                    )}
                                    name="paymentMethod"
                                    onChange={(option) => {
                                      const nextPaymentMethod = option?.value || "";
                                      setFieldValue("paymentMethod", nextPaymentMethod);

                                      if (nextPaymentMethod !== "bank_transfer") {
                                        setFieldValue("bank", "", false);
                                        setFieldValue("accountTitle", "", false);
                                        setFieldValue("iban", "", false);
                                      }
                                    }}
                                    onBlur={handleBlur}
                                    error={errors.paymentMethod}
                                    touched={touched.paymentMethod}
                                    isSearch={false}
                                    tabIndex={stepTwoTabOrder.paymentMethod}
                                  />
                                </Col>

                                <Col md={6}>
                                  <Select
                                    label={renderFieldLabel("Payment Terms (Days)")}
                                    placeholder="Select Payment Terms"
                                    options={paymentTermsOptions}
                                    value={pickOption(
                                      paymentTermsOptions,
                                      values.paymentTerms
                                    )}
                                    name="paymentTerms"
                                    onChange={(option) =>
                                      setFieldValue("paymentTerms", option?.value || "")
                                    }
                                    onBlur={handleBlur}
                                    error={errors.paymentTerms}
                                    touched={touched.paymentTerms}
                                    isSearch={false}
                                    tabIndex={stepTwoTabOrder.paymentTerms}
                                  />
                                </Col>

                                {values.paymentMethod === "bank_transfer" && (
                                  <>
                                    <Col md={6}>
                                      <Select
                                        label={renderFieldLabel("Bank")}
                                        placeholder="Select Bank"
                                        options={banksDropdownData}
                                        loading={customerDropdownLoading}
                                        value={pickOption(banksDropdownData, values.bank)}
                                        name="bank"
                                        onChange={(option) =>
                                          setFieldValue("bank", option?.value || "")
                                        }
                                        onBlur={handleBlur}
                                        error={errors.bank}
                                        touched={touched.bank}
                                        isSearch={false}
                                        tabIndex={stepTwoTabOrder.bank}
                                      />
                                    </Col>

                                    <Col md={6}>
                                      <Input
                                        label={renderFieldLabel("Account Title")}
                                        type="text"
                                        placeholder="XYZ Company Ltd."
                                        name="accountTitle"
                                        value={values.accountTitle}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.accountTitle}
                                        touched={touched.accountTitle}
                                        tabIndex={stepTwoTabOrder.accountTitle}
                                      />
                                    </Col>

                                    <Col md={6}>
                                      <Input
                                        label={renderFieldLabel("IBAN/Account Number")}
                                        type="text"
                                        placeholder="PK00XXXX"
                                        name="iban"
                                        value={values.iban}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.iban}
                                        touched={touched.iban}
                                        tabIndex={stepTwoTabOrder.iban}
                                      />
                                    </Col>
                                  </>
                                )}
                              </Row>

                              <div className="d-flex align-items-center gap-2 pricing_template_step_actions">
                                <Button
                                  cancelBtn={handlePrevious}
                                  submitBtn={handleSubmit}
                                  submitText={isSubmitting ? "Saving..." : "Save"}
                                  type="button"
                                  shouldRow={false}
                                  loading={isSubmitting}
                                  disabled={isSubmitting}
                                  cancelBtnText="Previous"
                                  cancelTabIndex={stepTwoTabOrder.next}
                                  submitTabIndex={stepTwoTabOrder.next + 1}
                                />
                              </div>
                            </div>
                          )}

                          {currentFormStep === ATTACHMENTS_STEP && (
                            <div className="driver_form_section">
                              <div className="driver_form_section_header">
                                <h3>Attachments</h3>
                                <p>Upload documents related to this contract.</p>
                              </div>
                              <Row>
                                <Col md={12}>
                                  <FileUpload
                                    destination="contract"
                                    files={values.attachments || []}
                                    setFiles={updateAttachments}
                                    maxFileSize={10}
                                    className="issue_file_upload create_vehicle_upload pricing_template_upload"
                                    tabIndex={stepAttachmentsTabOrder.attachments}
                                  />
                                </Col>

                                <Col md={12}>
                                  <div className="d-flex align-items-center gap-2 pricing_template_step_actions">
                                    <Button
                                      cancelBtn={handlePrevious}
                                      submitBtn={handleNext}
                                      submitText="Next"
                                      type="button"
                                      shouldRow={false}
                                      cancelBtnText="Previous"
                                      submitTabIndex={stepAttachmentsTabOrder.next}
                                    />
                                  </div>
                                </Col>
                              </Row>
                            </div>
                          )}

                          {currentFormStep === COMPONENTS_FORMULA_STEP && (
                            <div className="driver_form_section pricing_template_step_placeholder">
                              <div className="driver_form_section_header">
                                <h3>Vehicle and Formula Configuration</h3>
                                <p>Add vehicle types and configure revenue formulas for this contract.</p>
                              </div>

                              <div className="pricing_template_table_wrapper mb-4" style={{ backgroundColor: "#fff", border: "1px solid #e5e7eb", borderRadius: "8px", overflow: "hidden" }}>
                                <table className="table" style={{ margin: 0, width: "100%", borderCollapse: "collapse" }}>
                                  <thead>
                                    <tr style={{ backgroundColor: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                                      <th style={{ padding: "12px 16px", color: "#4b5563", fontWeight: 600, fontSize: "14px", textAlign: "left" }}>VEHICLE TYPE</th>
                                      <th style={{ padding: "12px 16px", color: "#4b5563", fontWeight: 600, fontSize: "14px", textAlign: "left" }}>FORMULA STATUS</th>
                                      <th style={{ padding: "12px 16px", color: "#4b5563", fontWeight: 600, fontSize: "14px", textAlign: "right" }}>ACTIONS</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {values.charges && values.charges.length > 0 ? (
                                      values.charges.map((charge, index) => {
                                        const isConfigured = charge.formulaRules?.length > 0 || charge.monthlyCharges || charge.variableRate || charge.fixedCharges?.length > 0;
                                        return (
                                          <tr key={charge.id || index} style={{ borderBottom: "1px solid #f3f4f6" }}>
                                            <td style={{ padding: "16px", fontSize: "14px", color: "#1f2937", display: "flex", alignItems: "center", gap: "10px", fontWeight: "500" }}>
                                              <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "32px", height: "32px", borderRadius: "6px", backgroundColor: "#eff6ff", color: "#2563eb" }}>
                                                <PiTruckFill style={{ fontSize: "18px" }} />
                                              </span>
                                              {charge.vehicleType}
                                            </td>
                                            <td style={{ padding: "16px", verticalAlign: "middle" }}>
                                              <span
                                                style={{
                                                  display: "inline-flex",
                                                  alignItems: "center",
                                                  padding: "4px 12px",
                                                  borderRadius: "9999px",
                                                  fontSize: "12px",
                                                  fontWeight: 500,
                                                  backgroundColor: isConfigured ? "#d1fae5" : "#f3f4f6",
                                                  color: isConfigured ? "#065f46" : "#4b5563",
                                                }}
                                              >
                                                {isConfigured ? "Configured" : "Not Configured"}
                                              </span>
                                            </td>
                                            <td style={{ padding: "16px", textAlign: "right", verticalAlign: "middle" }}>
                                              <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "12px" }}>
                                                <button
                                                  type="button"
                                                  className="btn btn-outline-primary"
                                                  style={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    gap: "6px",
                                                    fontSize: "13px",
                                                    fontWeight: 500,
                                                    padding: "6px 12px",
                                                    borderRadius: "6px",
                                                    border: "1px solid #d1d5db",
                                                    backgroundColor: "#fff",
                                                    color: "#374151"
                                                  }}
                                                  onClick={() => {
                                                    setEditingChargeIndex(index);
                                                    const currentCharge = values.charges[index];
                                                    if (currentCharge) {
                                                      if ((!currentCharge.formulaTokens || currentCharge.formulaTokens.length === 0) && currentCharge.formulaRules && currentCharge.formulaRules.length > 0) {
                                                        setFieldValue(`charges[${index}].formulaTokens`, currentCharge.formulaRules[0].tokens || [], false);
                                                      }
                                                      if ((!currentCharge.shortagesFormulaTokens || currentCharge.shortagesFormulaTokens.length === 0) && currentCharge.shortagesFormulaRules && currentCharge.shortagesFormulaRules.length > 0) {
                                                        setFieldValue(`charges[${index}].shortagesFormulaTokens`, currentCharge.shortagesFormulaRules[0].tokens || [], false);
                                                      }
                                                    }
                                                  }}
                                                >
                                                  <PiPencilSimple /> Configure Formula
                                                </button>
                                                <button
                                                  type="button"
                                                  style={{
                                                    background: "none",
                                                    border: "none",
                                                    color: "#ef4444",
                                                    cursor: "pointer",
                                                    padding: "4px"
                                                  }}
                                                  onClick={() => {
                                                    const nextVehicleTypes = values.vehicleTypes.filter((vId) => vId !== charge.vehicleTypeId);
                                                    setFieldValue("vehicleTypes", nextVehicleTypes);
                                                    setFieldValue(
                                                      "charges",
                                                      values.charges.filter((_, idx) => idx !== index),
                                                      false
                                                    );
                                                  }}
                                                >
                                                  <PiTrash style={{ fontSize: "18px" }} />
                                                </button>
                                              </div>
                                            </td>
                                          </tr>
                                        );
                                      })
                                    ) : (
                                      <tr>
                                        <td colSpan="3" style={{ padding: "40px 16px", textAlign: "center", color: "#6b7280", fontSize: "14px" }}>
                                          No vehicle types added yet. Use the selector below to add one.
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>

                              <Row className="align-items-end mb-4">
                                <Col md={10}>
                                  <Select
                                    label={renderFieldLabel("Vehicle Type", false)}
                                    placeholder="Select Vehicle Type"
                                    options={unselectedVehicleTypeOptions}
                                    value={pickOption(unselectedVehicleTypeOptions, selectedAddVehicleType)}
                                    onChange={(option) =>
                                      setSelectedAddVehicleType(option?.value || "")
                                    }
                                    isSearch={true}
                                    className="vehicle-type-select-wrapper"
                                    customStyles={compactSelectStyles}
                                  />
                                </Col>
                                <Col md={2}>
                                  <button
                                    type="button"
                                    className="btn btn-primary w-100"
                                    style={{
                                      backgroundColor: "#4f46e5",
                                      borderColor: "#4f46e5",
                                      height: "44px",
                                      borderRadius: "6px",
                                      fontWeight: "600",
                                      fontSize: "14px",
                                      marginBottom: "15px",
                                    }}
                                    onClick={() => {
                                      if (!selectedAddVehicleType) return;
                                      const nextVehicleTypes = [...values.vehicleTypes, selectedAddVehicleType];
                                      setFieldValue("vehicleTypes", nextVehicleTypes);
                                      setFieldValue(
                                        "charges",
                                        syncChargesWithVehicleTypes(
                                          values.charges,
                                          nextVehicleTypes,
                                          vehicleTypeDropdownOptions
                                        ),
                                        false
                                      );
                                      setSelectedAddVehicleType("");
                                    }}
                                    disabled={!selectedAddVehicleType}
                                  >
                                    Add
                                  </button>
                                </Col>
                              </Row>

                              <div className="d-flex align-items-center gap-2 pricing_template_step_actions">
                                <Button
                                  cancelBtn={handlePrevious}
                                  submitBtn={handleNext}
                                  submitText="Next"
                                  type="button"
                                  shouldRow={false}
                                  cancelBtnText="Previous"
                                  submitTabIndex={stepThreeFooterActionsTabIndex + 1}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>

                <Col lg={3}>
                  <div className="driver_form_sidebar trip_cost_estimation_create_sidebar">
                    <div className="trip_notes_main_div create_vehicle_notes driver_notes_card trip_cost_estimation_notes_card">
                      <h5>Notes</h5>
                      <ReactQuill
                        placeholder="Notes if any..."
                        modules={modules}
                        formats={formats}
                        tabIndex={
                          currentFormStep === TEMPLATE_INFORMATION_STEP
                            ? stepOneTabOrder.notes
                            : currentFormStep === PAYMENT_MODE_STEP
                              ? stepTwoTabOrder.notes
                              : currentFormStep === ATTACHMENTS_STEP
                                ? stepAttachmentsTabOrder.notes
                                : stepThreeNotesTabIndex
                        }
                        value={values.notes || ""}
                        onChange={(content) => setFieldValue("notes", content)}
                        className="react_quill_input notes_quill_input quill-small-font trip_quill_input"
                      />
                      {touched.notes && errors.notes ? (
                        <div
                          className="error-message"
                          style={{
                            color: "#ff0000de",
                            fontSize: "12px",
                            margin: "8px 12px 0",
                          }}
                        >
                          {errors.notes}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </Col>
              </Row>
            </div>

            {editingChargeIndex !== null && values.charges[editingChargeIndex] && (() => {
              const index = editingChargeIndex;
              const charge = values.charges[index];
              const formulaError = validateFormulaTokens(charge.formulaTokens || []);
              const shortagesFormulaError =
                charge.shortagesFormulaTokens && charge.shortagesFormulaTokens.length > 0
                  ? validateFormulaTokens(charge.shortagesFormulaTokens)
                  : null;

              const handleAddChargeClick = () => {
                if (!tempChargeType || !tempChargeValue) return;
                const val = Number(tempChargeValue);

                if (tempChargeType === "per_km" || tempChargeType === "per_ltr") {
                  setFieldValue(`charges[${index}].variableType`, tempChargeType);
                  setFieldValue(`charges[${index}].variableRate`, val);
                } else {
                  const existingFixed = charge.fixedCharges || [];
                  const existsIdx = existingFixed.findIndex((f) => f.fixedType === tempChargeType);
                  if (existsIdx > -1) {
                    setFieldValue(`charges[${index}].fixedCharges[${existsIdx}].fixedCharge`, val);
                  } else {
                    const newFixed = {
                      id: `fixed-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                      fixedType: tempChargeType,
                      fixedCharge: val,
                    };
                    setFieldValue(`charges[${index}].fixedCharges`, [...existingFixed, newFixed]);
                  }
                }
                setTempChargeType("");
                setTempChargeValue("");
              };

              const handleRemoveVariableCharge = () => {
                setFieldValue(`charges[${index}].variableType`, "");
                setFieldValue(`charges[${index}].variableRate`, "");
                const nextTokens = (charge.formulaTokens || []).filter((t) => t.type !== "variableRate");
                setFieldValue(`charges[${index}].formulaTokens`, nextTokens, false);
              };

              const handleRemoveFixedChargeItem = (fixedId, fixedType) => {
                const nextFixed = (charge.fixedCharges || []).filter((fc) => fc.id !== fixedId);
                setFieldValue(`charges[${index}].fixedCharges`, nextFixed);
                const nextTokens = (charge.formulaTokens || []).filter(
                  (t) => !(t.type === "fixedCharge" && t.fixedType === fixedType)
                );
                setFieldValue(`charges[${index}].formulaTokens`, nextTokens, false);
              };

              const handleAddShortageClick = () => {
                if (!tempShortageType || !tempShortageValue || !tempShortageBasis) return;
                const newShortage = {
                  id: `shortage-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                  shortageType: tempShortageType,
                  deductionValue: Number(tempShortageValue),
                  basis: tempShortageBasis,
                };
                const nextShortages = [...(charge.shortages || []), newShortage];
                setFieldValue(`charges[${index}].shortages`, nextShortages);
                setFieldValue(`charges[${index}].chargeShortage`, true);

                setTempShortageType("");
                setTempShortageValue("");
                setTempShortageBasis("");
              };

              const handleDeleteShortage = (shortageId) => {
                const nextShortages = (charge.shortages || []).filter((s) => s.id !== shortageId);
                setFieldValue(`charges[${index}].shortages`, nextShortages);
                if (nextShortages.length === 0) {
                  setFieldValue(`charges[${index}].chargeShortage`, false);
                }
              };

              const handleSaveFormulaAndClose = async () => {
                setFieldTouched(`charges[${index}].numberOfVehicle`, true, true);
                setFieldTouched(`charges[${index}].monthlyCharges`, true, true);

                const validationErrors = await validateForm();
                const currentChargeErrors = validationErrors?.charges?.[index];

                if (currentChargeErrors && Object.keys(currentChargeErrors).length > 0) {
                  return;
                }

                const currentTokens = charge.formulaTokens || [];
                const err = validateFormulaTokens(currentTokens);
                if (err) {
                  setFieldTouched(`charges[${index}].formulaTokens`, true, false);
                  setFieldError(`charges[${index}].formulaTokens`, err);
                  return;
                }

                setFieldError(`charges[${index}].formulaTokens`, undefined);

                const currentShortagesTokens = charge.shortagesFormulaTokens || [];
                const hasShortagesTokens = currentShortagesTokens.length > 0;

                if (hasShortagesTokens) {
                  const shortagesErr = validateFormulaTokens(currentShortagesTokens);
                  if (shortagesErr) {
                    setFieldTouched(`charges[${index}].shortagesFormulaTokens`, true, false);
                    setFieldError(`charges[${index}].shortagesFormulaTokens`, shortagesErr);
                    return;
                  }

                  setFieldError(`charges[${index}].shortagesFormulaTokens`, undefined);
                  setFieldValue(`charges[${index}].chargeShortage`, true, false);
                  setFieldValue(
                    `charges[${index}].shortagesFormulaRules`,
                    [
                      {
                        id: `rule-shortage-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
                        tokens: currentShortagesTokens.map((token) => ({ ...token })),
                        displayText: buildShortagesFormulaDisplay(index, currentShortagesTokens),
                      },
                    ],
                    false
                  );
                } else {
                  setFieldValue(`charges[${index}].chargeShortage`, false, false);
                  setFieldValue(`charges[${index}].shortagesFormulaTokens`, [], false);
                  setFieldValue(`charges[${index}].shortagesFormulaRules`, [], false);
                }

                setFieldValue(
                  `charges[${index}].formulaRules`,
                  [
                    {
                      id: `rule-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
                      tokens: currentTokens.map((token) => ({ ...token })),
                      displayText: buildFormulaDisplay(index, currentTokens),
                    },
                  ],
                  false
                );
                setEditingChargeIndex(null);
              };

              const activeComponents = [];
              if (charge.variableType) {
                activeComponents.push({
                  type: "variable",
                  label: getVariableTypeLabel(charge.variableType),
                  value: charge.variableRate,
                  variableType: charge.variableType,
                });
              }
              (charge.fixedCharges || []).forEach((fc, fcIdx) => {
                activeComponents.push({
                  type: "fixed",
                  label: getFixedTypeLabel(fc.fixedType),
                  value: fc.fixedCharge,
                  id: fc.id,
                  fixedType: fc.fixedType,
                });
              });

              return (
                <Drawer
                  placement="right"
                  onClose={() => setEditingChargeIndex(null)}
                  open={editingChargeIndex !== null}
                  width={850}
                  closable={false}
                  className="formula-builder-drawer"
                  title={
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                      <div>
                        <div className="formula-drawer-header-title">
                          <h4 style={{ margin: 0, fontWeight: 700, fontSize: "20px" }}>Revenue Formula Builder</h4>
                          <span className="vehicle-type-badge">{charge.vehicleType}</span>
                        </div>
                        <div className="formula-drawer-header-subtitle">
                          Define charges, compose the revenue formula and test it with sample trip data
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setEditingChargeIndex(null)}
                        style={{
                          border: "none",
                          background: "#f3f4f6",
                          borderRadius: "50%",
                          width: "32px",
                          height: "32px",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          color: "#4b5563",
                          padding: 0,
                        }}
                      >
                        <PiXBold style={{ fontSize: "16px" }} />
                      </button>
                    </div>
                  }
                  footer={
                    <div className="drawer-footer-actions">
                      <button
                        type="button"
                        className="footer-btn-back"
                        onClick={() => setEditingChargeIndex(null)}
                      >
                        <PiArrowLeft /> Back
                      </button>
                      <button
                        type="button"
                        className="footer-btn-save"
                        onClick={handleSaveFormulaAndClose}
                      >
                        Save Formula
                      </button>
                    </div>
                  }
                >
                  <style>{`
                    .formula-builder-drawer .ant-drawer-content-wrapper {
                      box-shadow: -10px 0 30px rgba(0, 0, 0, 0.08) !important;
                    }
                    .formula-builder-drawer .ant-drawer-header {
                      border-bottom: 1px solid #f3f4f6 !important;
                      padding: 20px 24px !important;
                    }
                    .formula-builder-drawer .ant-drawer-body {
                      background-color: #f9fafb !important;
                      padding: 24px !important;
                      display: flex;
                      flex-direction: column;
                      gap: 24px;
                      overflow-y: auto;
                    }
                    .formula-drawer-header-title {
                      display: flex;
                      align-items: center;
                      gap: 12px;
                    }
                    .vehicle-type-badge {
                      background-color: #eff6ff;
                      color: #2563eb;
                      font-size: 12px;
                      font-weight: 600;
                      padding: 4px 10px;
                      border-radius: 4px;
                      border: 1px solid #bfdbfe;
                    }
                    .formula-drawer-header-subtitle {
                      font-size: 13px;
                      color: #6b7280;
                      margin-top: 4px;
                      font-weight: 400;
                    }
                    .formula-drawer-card {
                      background: #ffffff;
                      border: 1px solid #e5e7eb;
                      border-radius: 6px;
                      padding: 24px;
                      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
                    }
                    .formula-drawer-card-header {
                      display: flex;
                      align-items: flex-start;
                      margin-bottom: 20px;
                    }
                    .formula-drawer-step-badge {
                      display: inline-flex;
                      align-items: center;
                      justify-content: center;
                      width: 26px;
                      height: 26px;
                      background-color: #eff6ff;
                      color: #2563eb;
                      font-weight: 700;
                      font-size: 13px;
                      border-radius: 4px;
                      margin-right: 12px;
                      flex-shrink: 0;
                    }
                    .formula-drawer-card-title-group h5 {
                      font-size: 16px;
                      font-weight: 700;
                      color: #1f2937;
                      margin: 0;
                    }
                    .formula-drawer-card-title-group p {
                      font-size: 13px;
                      color: #6b7280;
                      margin: 4px 0 0 0;
                    }
                    .formula-drawer-grid-row {
                      display: flex;
                      gap: 12px;
                      align-items: flex-end;
                      flex-wrap: wrap;
                      margin-bottom: 16px;
                    }
                    .formula-drawer-grid-col {
                      flex: 1;
                      min-width: 150px;
                    }
                    .formula-drawer-grid-col .input_wrapper {
                      position: relative;
                      margin-bottom: 0px !important;
                    }
                    .formula-drawer-grid-col .error-message {
                      position: absolute;
                      top: 100%;
                      left: 0px !important;
                      right: 0px !important;
                      margin-left: 0px !important;
                      color: #ef4444 !important;
                      margin-top: 2px !important;
                      font-size: 11px !important;
                      white-space: normal !important;
                      line-height: 1.2 !important;
                      z-index: 10;
                    }
                    .formula-drawer-grid-col-btn {
                      flex-shrink: 0;
                    }
                    .add-charge-btn {
                      background-color: #2563eb;
                      color: #ffffff;
                      border: none;
                      height: 38px;
                      border-radius: 4px;
                      font-weight: 600;
                      font-size: 13px;
                      padding: 0 16px;
                      display: inline-flex;
                      align-items: center;
                      justify-content: center;
                      gap: 6px;
                      cursor: pointer;
                      transition: all 0.2s ease;
                    }
                    .add-charge-btn:hover {
                      background-color: #1d4ed8;
                    }
                     .charges-builder-group {
                       display: flex;
                       gap: 16px;
                       margin-bottom: 20px;
                       align-items: stretch;
                     }
                     .vehicle-settings-box .formula-drawer-grid-row {
                       display: flex;
                       flex-direction: column;
                       gap: 12px;
                       align-items: stretch;
                       margin-bottom: 0;
                     }
                     .vehicle-settings-box .error-message {
                       position: static !important;
                       display: block !important;
                       color: #ef4444 !important;
                       margin-top: 4px !important;
                       font-size: 11px !important;
                       white-space: normal !important;
                     }
                    .component-composer-box {
                      flex: 1.5;
                      border: 1px solid #e5e7eb;
                      border-radius: 6px;
                      padding: 16px;
                      background-color: #f9fafb;
                    }
                    .vehicle-settings-box {
                      flex: 1;
                      border: 1px solid #e5e7eb;
                      border-radius: 6px;
                      padding: 16px;
                      background-color: #f9fafb;
                    }
                    .sub-group-title {
                      font-size: 11px;
                      font-weight: 700;
                      color: #4b5563;
                      margin-top: 0;
                      margin-bottom: 12px;
                      text-transform: uppercase;
                      letter-spacing: 0.05em;
                    }
                    .empty-placeholder {
                      border: 1px dashed #d1d5db;
                      border-radius: 4px;
                      padding: 20px;
                      text-align: center;
                      color: #9ca3af;
                      font-size: 13px;
                      background-color: #fafafa;
                    }
                    .added-components-card {
                      border: 1px solid #e5e7eb;
                      background-color: #f9fafb;
                      padding: 16px;
                      border-radius: 6px;
                      margin-top: 12px;
                    }
                    .active-components-container {
                      display: flex;
                      flex-wrap: wrap;
                      gap: 12px;
                      margin-top: 8px;
                    }
                    .pricing-component-card {
                      background: #ffffff;
                      border: 1px solid #e5e7eb;
                      border-radius: 6px;
                      padding: 12px 16px;
                      width: calc(33.33% - 8px);
                      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
                      display: flex;
                      flex-direction: column;
                      gap: 6px;
                      position: relative;
                    }
                    .pricing-component-card .card-top-row {
                      display: flex;
                      justify-content: space-between;
                      align-items: center;
                      width: 100%;
                    }
                    .pricing-component-card .card-badge-name {
                      font-size: 11px;
                      font-weight: 700;
                      text-transform: uppercase;
                      letter-spacing: 0.05em;
                    }
                    .pricing-component-card.is-variable .card-badge-name {
                      color: #10b981;
                    }
                    .pricing-component-card.is-fixed .card-badge-name {
                      color: #2563eb;
                    }
                    .pricing-component-card.is-shortage .card-badge-name {
                      color: #ef4444;
                    }
                    .pricing-component-card .card-remove-btn {
                      background: none;
                      border: none;
                      cursor: pointer;
                      color: #9ca3af;
                      padding: 0;
                      display: inline-flex;
                      align-items: center;
                      transition: color 0.2s;
                    }
                    .pricing-component-card .card-remove-btn:hover {
                      color: #ef4444;
                    }
                    .pricing-component-card .card-middle-row {
                      font-size: 16px;
                      font-weight: 700;
                      color: #1f2937;
                    }
                    .pricing-component-card .card-bottom-row {
                      font-size: 12px;
                      color: #6b7280;
                    }
                    .category-tab-bar {
                      display: flex;
                      gap: 12px;
                      border-bottom: 1px solid #e5e7eb;
                      padding-bottom: 12px;
                      margin-bottom: 16px;
                    }
                    .category-tab-btn {
                      display: inline-flex;
                      align-items: center;
                      gap: 8px;
                      padding: 6px 12px;
                      border-radius: 4px;
                      font-size: 13px;
                      font-weight: 500;
                      background-color: #ffffff;
                      border: 1px solid #e5e7eb;
                      color: #4b5563;
                      cursor: pointer;
                      transition: all 0.2s;
                    }
                    .category-tab-btn:hover {
                      border-color: #d1d5db;
                      background-color: #f9fafb;
                    }
                    .category-tab-btn.is-active {
                      border-color: #2563eb;
                      background-color: #eff6ff;
                      color: #2563eb;
                    }
                    .category-dot {
                      width: 8px;
                      height: 8px;
                      border-radius: 50%;
                      display: inline-block;
                    }
                    .dot-trip-data { background-color: #3b82f6; }
                    .dot-charges { background-color: #10b981; }
                    .dot-constants { background-color: #8b5cf6; }
                    .dot-operators { background-color: #f59e0b; }
                    .dot-brackets { background-color: #6366f1; }
                    
                    .tab-popover-wrapper {
                      position: relative;
                      display: inline-block;
                    }
                    .composer-popover-dropdown {
                      position: absolute;
                      top: 100%;
                      left: 0;
                      background-color: #ffffff;
                      border: 1px solid #e5e7eb;
                      border-radius: 6px;
                      padding: 16px;
                      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
                      z-index: 100;
                      min-width: 300px;
                      display: flex;
                      flex-direction: column;
                    }
                    .popover-heading {
                      font-size: 11px;
                      font-weight: 700;
                      color: #9ca3af;
                      text-transform: uppercase;
                      letter-spacing: 0.05em;
                      display: block;
                      margin-bottom: 8px;
                    }
                    .popover-grid {
                      display: flex;
                      flex-direction: column;
                      gap: 6px;
                      max-height: 200px;
                      overflow-y: auto;
                    }
                    .popover-grid-item {
                      background: none;
                      border: none;
                      color: #374151;
                      padding: 8px 12px;
                      font-size: 13px;
                      font-weight: 500;
                      text-align: left;
                      border-radius: 4px;
                      cursor: pointer;
                      transition: all 0.15s ease;
                    }
                    .popover-grid-item:hover {
                      background-color: #f3f4f6;
                    }
                    .popover-text-input {
                      flex: 1;
                      height: 36px;
                      border: 1px solid #d1d5db;
                      border-radius: 4px;
                      padding: 0 12px;
                      font-size: 13px;
                      outline: none;
                    }
                    .popover-text-input:focus {
                      border-color: #2563eb;
                    }
                    .popover-add-btn {
                      background-color: #2563eb;
                      color: #ffffff;
                      border: none;
                      padding: 0 16px;
                      height: 36px;
                      border-radius: 4px;
                      font-weight: 600;
                      font-size: 13px;
                      cursor: pointer;
                    }
                    .popover-add-btn:hover:not(:disabled) {
                      background-color: #1d4ed8;
                    }
                    .popover-add-btn:disabled {
                      opacity: 0.5;
                      cursor: not-allowed;
                    }
                    .popover-operators-grid {
                      display: grid;
                      grid-template-columns: repeat(4, 1fr);
                      gap: 8px;
                    }
                    .popover-operator-btn {
                      background-color: #ffffff;
                      border: 1px solid #e5e7eb;
                      color: #374151;
                      height: 38px;
                      font-size: 15px;
                      font-weight: 600;
                      border-radius: 4px;
                      cursor: pointer;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      transition: all 0.2s;
                    }
                    .popover-operator-btn:hover {
                      background-color: #f3f4f6;
                      border-color: #d1d5db;
                    }
                    .popover-empty {
                      font-size: 13px;
                      color: #6b7280;
                      padding: 8px 0;
                    }
                    .formula-composer-workspace {
                      border: 1px solid #e5e7eb;
                      border-radius: 6px;
                      padding: 16px;
                      background-color: #ffffff;
                      min-height: 120px;
                      display: flex;
                      flex-direction: column;
                      justify-content: space-between;
                    }
                    .composer-tokens-list {
                      display: flex;
                      flex-wrap: wrap;
                      gap: 6px;
                    }
                    .composer-token-pill {
                      display: inline-flex;
                      align-items: center;
                      gap: 6px;
                      padding: 6px 10px;
                      border-radius: 4px;
                      font-size: 13px;
                      font-weight: 500;
                      cursor: pointer;
                      transition: all 0.2s;
                    }
                    .composer-token-pill.type-tripData {
                      background-color: #eff6ff;
                      border: 1px solid #bfdbfe;
                      color: #1e40af;
                    }
                    .composer-token-pill.type-variableRate {
                      background-color: #e0f2fe;
                      border: 1px solid #bae6fd;
                      color: #0369a1;
                    }
                    .composer-token-pill.type-fixedCharge {
                      background-color: #f0fdf4;
                      border: 1px solid #bbf7d0;
                      color: #166534;
                    }
                    .composer-token-pill.type-hardcodedValue {
                      background-color: #f5f3ff;
                      border: 1px solid #ddd6fe;
                      color: #5b21b6;
                    }
                    .composer-token-pill.type-operator {
                      background-color: #fffbeb;
                      border: 1px solid #fde68a;
                      color: #92400e;
                    }
                    .composer-token-pill:hover {
                      opacity: 0.8;
                      text-decoration: line-through;
                    }
                    .composer-workspace-footer {
                      display: flex;
                      justify-content: flex-end;
                      gap: 8px;
                      margin-top: 12px;
                      border-top: 1px solid #f3f4f6;
                      padding-top: 12px;
                    }
                    .action-btn-outline {
                      border: 1px solid #d1d5db;
                      background: #ffffff;
                      color: #374151;
                      padding: 6px 12px;
                      border-radius: 4px;
                      font-size: 13px;
                      font-weight: 500;
                      cursor: pointer;
                      display: inline-flex;
                      align-items: center;
                      gap: 4px;
                    }
                    .action-btn-outline:hover:not(:disabled) {
                      background-color: #f9fafb;
                    }
                    .action-btn-outline:disabled {
                      opacity: 0.5;
                      cursor: not-allowed;
                    }
                    .live-preview-section {
                      margin-top: 20px;
                    }
                    .live-preview-label {
                      font-size: 11px;
                      font-weight: 700;
                      color: #9ca3af;
                      text-transform: uppercase;
                      letter-spacing: 0.05em;
                      margin-bottom: 6px;
                    }
                    .live-preview-box {
                      background-color: #f9fafb;
                      border: 1px solid #e5e7eb;
                      border-radius: 6px;
                      padding: 16px;
                      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
                      font-size: 14px;
                      color: #1f2937;
                      font-weight: 500;
                      min-height: 54px;
                    }
                    .formula-error-alert {
                      margin-top: 12px;
                      display: flex;
                      align-items: center;
                      gap: 8px;
                      padding: 10px 14px;
                      background-color: #fef2f2;
                      border: 1px solid #fee2e2;
                      border-radius: 6px;
                      color: #b91c1c;
                      font-size: 13px;
                      font-weight: 500;
                    }
                    .formula-success-alert {
                      margin-top: 12px;
                      display: flex;
                      align-items: center;
                      gap: 8px;
                      padding: 10px 14px;
                      background-color: #f0fdf4;
                      border: 1px solid #bbf7d0;
                      border-radius: 6px;
                      color: #166534;
                      font-size: 13px;
                      font-weight: 500;
                    }
                    .shortages-list {
                      margin-top: 0;
                      display: flex;
                      flex-wrap: wrap;
                      gap: 12px;
                    }
                    .drawer-footer-actions {
                      display: flex;
                      justify-content: space-between;
                      align-items: center;
                      width: 100%;
                    }
                    .footer-btn-back {
                      border: 1px solid #d1d5db;
                      background-color: #ffffff;
                      color: #374151;
                      font-weight: 600;
                      padding: 8px 16px;
                      border-radius: 4px;
                      cursor: pointer;
                      display: inline-flex;
                      align-items: center;
                      gap: 6px;
                      font-size: 14px;
                    }
                    .footer-btn-back:hover {
                      background-color: #f9fafb;
                    }
                    .footer-btn-save {
                      background-color: #2563eb;
                      color: #ffffff;
                      border: none;
                      font-weight: 600;
                      padding: 8px 20px;
                      border-radius: 4px;
                      cursor: pointer;
                      font-size: 14px;
                      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
                      transition: background-color 0.2s;
                    }
                    .footer-btn-save:hover {
                      background-color: #1d4ed8;
                    }
                    .vehicle-type-select-wrapper .input_container {
                      height: 44px !important;
                      padding: 2px 6px !important;
                      display: flex;
                      flex-direction: column;
                      justify-content: center;
                    }
                  `}</style>

                  {/* Section 1: Components & Charges */}
                  <div className="formula-drawer-card">
                    <div className="formula-drawer-card-header">
                      <div className="formula-drawer-step-badge">1</div>
                      <div className="formula-drawer-card-title-group">
                        <h5>Components & Charges</h5>
                        <p>Define the pricing components used by this vehicle type</p>
                      </div>
                    </div>

                    <div className="charges-builder-group">
                      <div className="component-composer-box">
                        <div className="sub-group-title">Trip Fixed Charges</div>
                        <div className="formula-drawer-grid-row" style={{ marginBottom: 0 }}>
                          <div className="formula-drawer-grid-col" style={{ flex: 1.5 }}>
                            <Select
                              label={renderFieldLabel("Charges Type", false)}
                              placeholder="Select Type"
                              options={componentTypeOptions}
                              value={pickOption(componentTypeOptions, tempChargeType)}
                              onChange={(option) => setTempChargeType(option?.value || "")}
                              isSearch={false}
                              required={false}
                              menuPlacement="auto"
                              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                            />
                          </div>
                          <div className="formula-drawer-grid-col">
                            <Input
                              label={renderFieldLabel("Value", false)}
                              type="number"
                              placeholder="0.00"
                              value={tempChargeValue}
                              onChange={(e) => setTempChargeValue(e.target.value)}
                              required={false}
                            />
                          </div>
                          <div className="formula-drawer-grid-col-btn" style={{ marginBottom: 0 }}>
                            <button
                              type="button"
                              className="add-charge-btn"
                              disabled={!tempChargeType || !tempChargeValue}
                              onClick={handleAddChargeClick}
                            >
                              <PiPlus /> Add Charge
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="vehicle-settings-box">
                        <div className="sub-group-title">Vehicle Settings</div>
                        <div className="formula-drawer-grid-row" style={{ marginBottom: 0 }}>
                          <div className="formula-drawer-grid-col">
                            <Input
                              label={renderFieldLabel("No. of Vehicles")}
                              type="number"
                              placeholder="0"
                              name={`charges[${index}].numberOfVehicle`}
                              value={charge.numberOfVehicle}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors?.charges?.[index]?.numberOfVehicle}
                              touched={touched?.charges?.[index]?.numberOfVehicle}
                            />
                          </div>
                          <div className="formula-drawer-grid-col">
                            <Input
                              label={renderFieldLabel("Monthly Rent", false)}
                              type="number"
                              placeholder="0.00"
                              name={`charges[${index}].monthlyCharges`}
                              value={charge.monthlyCharges}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors?.charges?.[index]?.monthlyCharges}
                              touched={touched?.charges?.[index]?.monthlyCharges}
                              required={false}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {activeComponents.length > 0 ? (
                      <div className="added-components-card">
                        <div className="active-components-container" style={{ marginTop: 0 }}>
                          {activeComponents.map((item, itemIdx) => {
                            const isUsed = item.type === "variable"
                              ? (charge.formulaTokens || []).some((t) => t.type === "variableRate")
                              : (charge.formulaTokens || []).some(
                                (t) => t.type === "fixedCharge" && t.metadata?.fixedType === item.fixedType
                              );

                            return (
                              <div
                                key={item.id || `active-${itemIdx}`}
                                className={`pricing-component-card is-${item.type}`}
                              >
                                <div className="card-top-row">
                                  <span className="card-badge-name">
                                    {item.label.toUpperCase()}
                                  </span>
                                  <button
                                    type="button"
                                    className="card-remove-btn"
                                    onClick={() => {
                                      if (item.type === "variable") {
                                        handleRemoveVariableCharge();
                                      } else {
                                        handleRemoveFixedChargeItem(item.id, item.fixedType);
                                      }
                                    }}
                                  >
                                    <PiXBold style={{ fontSize: "10px" }} />
                                  </button>
                                </div>
                                <div className="card-middle-row">
                                  Rs {item.value}
                                </div>
                                <div className="card-bottom-row">
                                  {isUsed ? "Used in formula" : "Not used in formula"}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="empty-placeholder" style={{ marginTop: "12px", width: "100%" }}>
                        No components yet. Add a charge above (e.g. Per KM = 150).
                      </div>
                    )}
                  </div>

                  {/* Section 2: Formula Composer */}
                  <div className="formula-drawer-card">
                    <div className="formula-drawer-card-header">
                      <div className="formula-drawer-step-badge">2</div>
                      <div className="formula-drawer-card-title-group">
                        <h5>Formula Composer</h5>
                        <p>Click a category, then a token, to build the formula. Click a token to remove it.</p>
                      </div>
                    </div>

                    <div className="category-tab-bar" style={{ display: "flex", gap: "12px", borderBottom: "1px solid #e5e7eb", paddingBottom: "12px", marginBottom: "16px" }}>

                      {/* Trip Data Wrapper */}
                      <div className="tab-popover-wrapper">
                        <button
                          type="button"
                          className={`category-tab-btn ${activeCategory === "tripData" ? "is-active" : ""}`}
                          onClick={() => setActiveCategory(activeCategory === "tripData" ? null : "tripData")}
                        >
                          <span className="category-dot dot-trip-data"></span>
                          Trip Data
                        </button>
                        {activeCategory === "tripData" && (
                          <div className="composer-popover-dropdown">
                            <span className="popover-heading">SELECT TRIP DATA</span>
                            <div className="popover-grid">
                              {tripDataTypeOptions.map((opt) => (
                                <button
                                  key={opt.value}
                                  type="button"
                                  className="popover-grid-item"
                                  onClick={() => {
                                    appendFormulaToken(index, createFormulaToken("tripData", opt.value, opt.label));
                                    setActiveCategory(null);
                                  }}
                                >
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Charges Wrapper */}
                      <div className="tab-popover-wrapper">
                        <button
                          type="button"
                          className={`category-tab-btn ${activeCategory === "charges" ? "is-active" : ""}`}
                          onClick={() => setActiveCategory(activeCategory === "charges" ? null : "charges")}
                        >
                          <span className="category-dot dot-charges"></span>
                          Charges
                        </button>
                        {activeCategory === "charges" && (
                          <div className="composer-popover-dropdown">
                            <span className="popover-heading">SELECT A CHARGE</span>
                            {(() => {
                              const hasVar = !!charge.variableType;
                              const hasFixed = charge.fixedCharges && charge.fixedCharges.length > 0;
                              if (!hasVar && !hasFixed) {
                                return (
                                  <div className="popover-empty">
                                    No charges configured. Add a component in Section 1 first.
                                  </div>
                                );
                              }
                              return (
                                <div className="popover-grid">
                                  {hasVar && (
                                    <button
                                      type="button"
                                      className="popover-grid-item"
                                      onClick={() => {
                                        appendFormulaToken(
                                          index,
                                          createFormulaToken(
                                            "variableRate",
                                            charge.variableRate,
                                            `${getVariableTypeLabel(charge.variableType)} (${charge.variableRate})`
                                          )
                                        );
                                        setActiveCategory(null);
                                      }}
                                    >
                                      {getVariableTypeLabel(charge.variableType)} ({charge.variableRate})
                                    </button>
                                  )}
                                  {hasFixed &&
                                    charge.fixedCharges.map((fc, fcIdx) => (
                                      <button
                                        key={fc.id || fcIdx}
                                        type="button"
                                        className="popover-grid-item"
                                        onClick={() => {
                                          appendFormulaToken(
                                            index,
                                            createFormulaToken(
                                              "fixedCharge",
                                              fc.fixedCharge,
                                              `${getFixedTypeLabel(fc.fixedType)} (${fc.fixedCharge})`,
                                              { fixedType: fc.fixedType }
                                            )
                                          );
                                          setActiveCategory(null);
                                        }}
                                      >
                                        {getFixedTypeLabel(fc.fixedType)} ({fc.fixedCharge})
                                      </button>
                                    ))}
                                </div>
                              );
                            })()}
                          </div>
                        )}
                      </div>

                      {/* Constants Wrapper */}
                      <div className="tab-popover-wrapper">
                        <button
                          type="button"
                          className={`category-tab-btn ${activeCategory === "constants" ? "is-active" : ""}`}
                          onClick={() => setActiveCategory(activeCategory === "constants" ? null : "constants")}
                        >
                          <span className="category-dot dot-constants"></span>
                          Constants
                        </button>
                        {activeCategory === "constants" && (
                          <div className="composer-popover-dropdown">
                            <span className="popover-heading">ENTER A CONSTANT</span>
                            <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                              <input
                                type="number"
                                className="popover-text-input"
                                placeholder="e.g. 1.05"
                                value={charge.formulaHardcodedValue || ""}
                                onChange={(e) =>
                                  setFieldValue(`charges[${index}].formulaHardcodedValue`, e.target.value)
                                }
                              />
                              <button
                                type="button"
                                className="popover-add-btn"
                                onClick={() => {
                                  handleAddHardcodedValueToFormula(index);
                                  setActiveCategory(null);
                                }}
                                disabled={!String(charge.formulaHardcodedValue || "").trim()}
                              >
                                Add
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Operators Wrapper */}
                      <div className="tab-popover-wrapper">
                        <button
                          type="button"
                          className={`category-tab-btn ${activeCategory === "operators" ? "is-active" : ""}`}
                          onClick={() => setActiveCategory(activeCategory === "operators" ? null : "operators")}
                        >
                          <span className="category-dot dot-operators"></span>
                          Operators
                        </button>
                        {activeCategory === "operators" && (
                          <div className="composer-popover-dropdown" style={{ minWidth: "240px" }}>
                            <span className="popover-heading">OPERATORS & BRACKETS</span>
                            <div className="popover-operators-grid">
                              {["+", "-", "*", "/", "%", "(", ")"].map((operator) => (
                                <button
                                  key={operator}
                                  type="button"
                                  className="popover-operator-btn"
                                  onClick={() => {
                                    handleAddOperatorToFormula(index, operator);
                                    setActiveCategory(null);
                                  }}
                                >
                                  {operator === "/" ? "÷" : operator === "*" ? "×" : operator}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                    </div>

                    <div className="formula-composer-workspace">
                      <div className="composer-tokens-list">
                        {!charge.formulaTokens || charge.formulaTokens.length === 0 ? (
                          <div style={{ color: "#9ca3af", fontSize: "13px", padding: "8px 0" }}>
                            Start building: add Trip Data, Charges, Constants or Operators above.
                          </div>
                        ) : (
                          charge.formulaTokens.map((token, tokenIdx) => (
                            <span
                              key={token.id || `composer-${tokenIdx}`}
                              className={`composer-token-pill type-${token.type}`}
                              onClick={() => handleRemoveFormulaToken(index, tokenIdx)}
                              title="Click to remove"
                            >
                              {resolveFormulaTokenLabel(index, token)}
                            </span>
                          ))
                        )}
                      </div>

                      <div className="composer-workspace-footer">
                        <button
                          type="button"
                          className="action-btn-outline"
                          onClick={() => handleBackspaceFormula(index)}
                          disabled={!charge.formulaTokens || charge.formulaTokens.length === 0}
                        >
                          Backspace
                        </button>
                        <button
                          type="button"
                          className="action-btn-outline"
                          style={{ color: "#ef4444", borderColor: "#fecaca" }}
                          onClick={() => handleClearFormula(index)}
                          disabled={!charge.formulaTokens || charge.formulaTokens.length === 0}
                        >
                          Clear All
                        </button>
                      </div>
                    </div>

                    <div className="live-preview-section">
                      <div className="live-preview-label">Live Preview</div>
                      <div className="live-preview-box">
                        Revenue = {buildFormulaDisplay(index, charge.formulaTokens || []) || "..."}
                      </div>

                      {formulaError ? (
                        <div className="formula-error-alert">
                          <PiWarningCircle style={{ fontSize: "16px" }} />
                          {formulaError}
                        </div>
                      ) : (
                        charge.formulaTokens && charge.formulaTokens.length > 0 && (
                          <div className="formula-success-alert">
                            <PiCheckCircle style={{ fontSize: "16px" }} />
                            Formula is valid
                          </div>
                        )
                      )}
                    </div>
                  </div>

                  {/* Section 3: Shortages & Deductions */}
                  <div className="formula-drawer-card">
                    <div className="formula-drawer-card-header">
                      <div className="formula-drawer-step-badge">3</div>
                      <div className="formula-drawer-card-title-group">
                        <h5>Shortages & Deductions</h5>
                        <p>Define shortage charges deducted from the calculated revenue</p>
                      </div>
                    </div>

                    {/* Shortages Formula Composer */}
                    <div className="shortages-formula-builder-section" style={{ marginTop: "12px" }}>

                      <div className="category-tab-bar" style={{ display: "flex", gap: "12px", borderBottom: "1px solid #e5e7eb", paddingBottom: "12px", marginBottom: "16px" }}>

                        {/* Trip Data Wrapper */}
                        <div className="tab-popover-wrapper">
                          <button
                            type="button"
                            className={`category-tab-btn ${activeShortagesCategory === "tripData" ? "is-active" : ""}`}
                            onClick={() => setActiveShortagesCategory(activeShortagesCategory === "tripData" ? null : "tripData")}
                          >
                            <span className="category-dot dot-trip-data"></span>
                            Trip Data
                          </button>
                          {activeShortagesCategory === "tripData" && (
                            <div className="composer-popover-dropdown">
                              <span className="popover-heading">SELECT TRIP DATA</span>
                              <div className="popover-grid">
                                {storageTripDataTypeOptions.map((opt) => (
                                  <button
                                    key={opt.value}
                                    type="button"
                                    className="popover-grid-item"
                                    onClick={() => {
                                      appendShortagesFormulaToken(index, createFormulaToken("tripData", opt.value, opt.label));
                                      setActiveShortagesCategory(null);
                                    }}
                                  >
                                    {opt.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Operators Wrapper */}
                        <div className="tab-popover-wrapper">
                          <button
                            type="button"
                            className={`category-tab-btn ${activeShortagesCategory === "operators" ? "is-active" : ""}`}
                            onClick={() => setActiveShortagesCategory(activeShortagesCategory === "operators" ? null : "operators")}
                          >
                            <span className="category-dot dot-operators"></span>
                            Operators
                          </button>
                          {activeShortagesCategory === "operators" && (
                            <div className="composer-popover-dropdown" style={{ minWidth: "240px" }}>
                              <span className="popover-heading">OPERATORS & BRACKETS</span>
                              <div className="popover-operators-grid">
                                {["+", "-", "*", "/", "%", "(", ")"].map((operator) => (
                                  <button
                                    key={operator}
                                    type="button"
                                    className="popover-operator-btn"
                                    onClick={() => {
                                      handleAddOperatorToShortagesFormula(index, operator);
                                      setActiveShortagesCategory(null);
                                    }}
                                  >
                                    {operator === "/" ? "÷" : operator === "*" ? "×" : operator}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                      </div>

                      <div className="formula-composer-workspace">
                        <div className="composer-tokens-list">
                          {!charge.shortagesFormulaTokens || charge.shortagesFormulaTokens.length === 0 ? (
                            <div style={{ color: "#9ca3af", fontSize: "13px", padding: "8px 0" }}>
                              Start building: add Trip Data or Operators above.
                            </div>
                          ) : (
                            charge.shortagesFormulaTokens.map((token, tokenIdx) => (
                              <span
                                key={token.id || `composer-shortages-${tokenIdx}`}
                                className={`composer-token-pill type-${token.type}`}
                                onClick={() => handleRemoveShortagesFormulaToken(index, tokenIdx)}
                                title="Click to remove"
                              >
                                {resolveFormulaTokenLabel(index, token)}
                              </span>
                            ))
                          )}
                        </div>

                        <div className="composer-workspace-footer">
                          <button
                            type="button"
                            className="action-btn-outline"
                            onClick={() => handleBackspaceShortagesFormula(index)}
                            disabled={!charge.shortagesFormulaTokens || charge.shortagesFormulaTokens.length === 0}
                          >
                            Backspace
                          </button>
                          <button
                            type="button"
                            className="action-btn-outline"
                            style={{ color: "#ef4444", borderColor: "#fecaca" }}
                            onClick={() => handleClearShortagesFormula(index)}
                            disabled={!charge.shortagesFormulaTokens || charge.shortagesFormulaTokens.length === 0}
                          >
                            Clear All
                          </button>
                        </div>
                      </div>

                      <div className="live-preview-section">
                        <div className="live-preview-label">Live Preview</div>
                        <div className="live-preview-box">
                          Deduction = {buildShortagesFormulaDisplay(index, charge.shortagesFormulaTokens || []) || "..."}
                        </div>

                        {shortagesFormulaError ? (
                          <div className="formula-error-alert">
                            <PiWarningCircle style={{ fontSize: "16px" }} />
                            {shortagesFormulaError}
                          </div>
                        ) : (
                          charge.shortagesFormulaTokens && charge.shortagesFormulaTokens.length > 0 && (
                            <div className="formula-success-alert">
                              <PiCheckCircle style={{ fontSize: "16px" }} />
                              Formula is valid
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                </Drawer>
              );
            })()}
          </div>
        );
      }}
    </Formik>
  );
};

export default CreateContract;